import {
	ArrowLeft,
	ArrowRight,
	BadgeCheck,
	Bell,
	BookOpen,
	Check,
	CircleUserRound,
	ClipboardList,
	HandHeart,
	Home as HomeIcon,
	Image as ImageIcon,
	Leaf,
	LibraryBig,
	LockKeyhole,
	Mic,
	Pause,
	PenLine,
	Play,
	Plus,
	Send,
	Sparkles,
	Star,
	SunMedium,
	Trophy,
	Type,
	User,
	UsersRound,
	X,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useSWR from "swr";
import {
	createGroupReaction,
	createNote,
	createPracticeFeedback,
	createPracticeRecord,
	createRelationAsset,
	drawGrowthPractice,
	getGrowthBootstrap,
	growthBootstrapKey,
	joinGrowthGroup,
	toggleOngoingPractice,
	type ApiBadge,
	type ApiEvidenceRule,
	type ApiGroup,
	type ApiGrowthReport,
	type ApiOngoingPractice,
	type ApiPractice,
	type ApiProgram,
	type ApiRelationAsset,
	type ApiSubskill,
	type ApiTimelineItem,
} from "@/api/growth";
import {
	clearAuthingSession,
	loadAuthingSession,
	saveAuthingSession,
	type AuthingSession,
} from "@/lib/authing";
import "./Home.css";

type DimensionId = "aware" | "strength" | "relate" | "resil" | "hope";
type Tab = "home" | "practice" | "records" | "mine";
type Flow = "draw" | "detail" | "note" | null;
type RecordMode = "text" | "image" | "voice";
type NoteVisibility = "private" | "group" | "friend";
type RecordFilter = "all" | "practice" | "note" | "group" | "friend";
type GroupReactionType = "seen" | "support";

type Dimension = {
	id: DimensionId;
	name: string;
	short: string;
	Icon: LucideIcon;
	color: string;
	soft: string;
	deep: string;
	tag: string;
	blurb: string;
};

type Practice = {
	id: string;
	dim: DimensionId;
	title: string;
	sub: string;
	intro: string;
	share?: boolean;
	course: {
		title: string;
		len: string;
	};
	assess: {
		title: string;
		q: number;
	} | null;
	steps: string[];
	recordPrompt: string;
	source?: {
		appSurface: string;
		formTag: string;
		originalTheme: string;
		socialTag: string;
		theorySource: string;
	};
};

type Ongoing = {
	id: string;
	practiceId: string;
	days: number;
	week: number[];
	today: number;
	checked: boolean;
};

type TimelineItem = {
	id: string;
	kind: "practice" | "note";
	authorName?: string;
	dim: DimensionId | null;
	exerciseId?: string;
	feedbackScore?: number | null;
	friendName?: string | null;
	friendTag?: string | null;
	title?: string;
	time: string;
	text: string;
	image: string | null;
	reactions?: {
		seen: number;
		support: number;
	};
	targetGroupId?: string | null;
	targetGroupName?: string | null;
	visibility?: NoteVisibility;
	voice: number;
};

type Badge = {
	id: string;
	name: string;
	Icon: LucideIcon;
	got: boolean;
	dim: DimensionId;
	desc: string;
};

type RelationAsset = {
	id: string;
	name: string;
	tag: string;
	from: string;
	time: string;
	sourceKind?: "practice" | "note";
	sourceNoteId?: string | null;
	sourceRecordId?: string | null;
	visibility?: string;
};

type ProgramPlan = {
	blurb: string;
	currentDay: number;
	days: Array<{ day: number; exerciseId: string | null; focus: string; title: string }>;
	dim: DimensionId;
	done: number;
	gains: string[];
	id: string;
	nextExerciseId?: string;
	per: string;
	rewardText: string;
	title: string;
	total: number;
};

type GrowthGroup = {
	description: string;
	dim: DimensionId;
	id: string;
	joined: boolean;
	memberCount: number;
	name: string;
	sessionLabel: string;
	sessionTitle: string;
};

type ProfileStats = {
	avTxt: string;
	joined: string;
	name: string;
	steadyDays: number;
	totalPractice: number;
	wellbeing: number;
	mediaCount: number;
	notesCount: number;
	todayCompleted: number;
};

type GrowthReport = {
	focusDimension: DimensionId;
	headline: string;
	nextDecision: string;
	ranges: Array<{
		days: number;
		dimensions: Array<{ count: number; dimensionId: DimensionId }>;
		total: number;
	}>;
};

type SuccessState = {
	entry: TimelineItem;
	evidenceScore?: number;
	nextAction?: string;
	nextSuggestion?: string;
	practice: Practice;
	recordId?: string;
};

type PracticePayload = {
	mode: RecordMode;
	text: string;
	friendName?: string;
	friendTag?: string;
	image?: string | null;
	targetGroupId?: string | null;
	visibility?: NoteVisibility;
	voice?: number;
};

const cn = (...items: Array<string | false | null | undefined>) => items.filter(Boolean).join(" ");

const DIMENSIONS: Dimension[] = [
	{
		id: "aware",
		name: "积极觉察",
		short: "觉察",
		Icon: Sparkles,
		color: "oklch(0.72 0.18 48)",
		soft: "oklch(0.96 0.045 67)",
		deep: "oklch(0.47 0.13 43)",
		tag: "留意当下",
		blurb: "看见生活里被忽略的小变化。",
	},
	{
		id: "strength",
		name: "优势运用",
		short: "优势",
		Icon: SunMedium,
		color: "oklch(0.78 0.15 73)",
		soft: "oklch(0.97 0.05 76)",
		deep: "oklch(0.48 0.11 64)",
		tag: "发挥所长",
		blurb: "把自己的长处放进今天。",
	},
	{
		id: "relate",
		name: "关系联结",
		short: "联结",
		Icon: UsersRound,
		color: "oklch(0.72 0.17 25)",
		soft: "oklch(0.96 0.04 31)",
		deep: "oklch(0.46 0.13 24)",
		tag: "靠近彼此",
		blurb: "用一个真实动作连接某个人。",
	},
	{
		id: "resil",
		name: "韧性调适",
		short: "韧性",
		Icon: Leaf,
		color: "oklch(0.67 0.15 145)",
		soft: "oklch(0.95 0.045 142)",
		deep: "oklch(0.42 0.12 148)",
		tag: "重新站稳",
		blurb: "在不顺里找到下一步。",
	},
	{
		id: "hope",
		name: "希望行动",
		short: "希望",
		Icon: Send,
		color: "oklch(0.69 0.13 196)",
		soft: "oklch(0.95 0.04 197)",
		deep: "oklch(0.43 0.1 199)",
		tag: "迈出一步",
		blurb: "为期待的生活做一件小事。",
	},
];

const DIM = Object.fromEntries(DIMENSIONS.map((dimension) => [dimension.id, dimension])) as Record<
	DimensionId,
	Dimension
>;

const USER_INFO = {
	name: "练习者",
	avTxt: "练",
	joined: "正在同步成长档案",
	steadyDays: 0,
	totalPractice: 0,
	wellbeing: 0,
};

const getPublicProfileName = (session?: AuthingSession | null) => {
	const candidates = [session?.username, session?.nickname, session?.name]
		.map((item) => item?.trim())
		.filter(Boolean);
	const name = candidates.find((item) => item !== USER_INFO.name);
	if (name) return name;
	return USER_INFO.name;
};

// 成长档案 counts shown on 我的 (PDF).
const GROWTH_LEDGER: Array<{ dim: DimensionId; label: string; unit: string }> = [
	{ dim: "aware", label: "积极事件", unit: "条" },
	{ dim: "strength", label: "优势行动", unit: "次" },
	{ dim: "relate", label: "关系行动", unit: "次" },
	{ dim: "resil", label: "韧性练习", unit: "次" },
	{ dim: "hope", label: "希望行动", unit: "次" },
];

const SUMMARY_WORDS = ["平静", "清醒", "靠近", "松了一口气", "有力量", "继续"];
const RELATION_TAGS = ["朋友", "家人", "同事", "伴侣", "老师"];

const isDimensionId = (value: string): value is DimensionId => value in DIM;

const cleanPracticeCopy = (value: string) =>
	value.replaceAll("打卡", "记录").replaceAll("连续记录可视化", "形成练习证据");

type PracticeActionDesign = {
	recordPrompt: string;
	steps: string[];
};

const splitPracticeFragments = (value: string) =>
	cleanPracticeCopy(value)
		.split(/[；;。，]/u)
		.map((item) => item.trim())
		.filter(Boolean);

const makePracticeSearchText = (practice: ApiPractice) =>
	`${practice.title} ${practice.actionStrategy} ${practice.originalTheme} ${practice.formTag}`;

const DIMENSION_ACTION_DESIGN: Record<DimensionId, PracticeActionDesign> = {
	aware: {
		steps: [
			"选一个今天真实发生的小片段",
			"写下当时让你有感觉的细节",
			"停留 20 秒，标出它让你好在哪一点",
		],
		recordPrompt: "可以按三句写：发生了什么；哪个细节被我注意到；它让我多了一点什么。",
	},
	strength: {
		steps: [
			"选一件你刚做过或做得还不错的小事",
			"找出里面用到的一种优势或能力",
			"想一个明天能再次使用它的场景",
		],
		recordPrompt: "可以按三句写：我做了什么；我用到了什么优势；下次我准备怎么继续用它。",
	},
	relate: {
		steps: ["选一个今天想靠近一点的人", "做一个很小的表达、倾听或回应", "观察对方或自己的反应变化"],
		recordPrompt: "可以按三句写：我对谁做了什么；对方或我有什么反应；这让我更理解了什么关系需要。",
	},
	resil: {
		steps: [
			"写下一个今天真实存在的困难或情绪",
			"给它命名，并区分可控与不可控部分",
			"选择一个现在能做的支持动作",
		],
		recordPrompt:
			"可以按三句写：困难是什么；我现在的情绪叫什么；我下一步能照顾自己的一个动作是什么。",
	},
	hope: {
		steps: [
			"选一个你在意的小目标",
			"写出一条可能走得通的路径",
			"把路径缩成今天能完成的 10 分钟动作",
		],
		recordPrompt: "可以按三句写：我想靠近什么；有哪条路径；今天我先做哪一个最小动作。",
	},
};

const buildPracticeActionDesign = (practice: ApiPractice): PracticeActionDesign => {
	const text = makePracticeSearchText(practice);
	const fallback = isDimensionId(practice.dimensionId)
		? DIMENSION_ACTION_DESIGN[practice.dimensionId]
		: DIMENSION_ACTION_DESIGN.aware;
	const fragments = splitPracticeFragments(practice.actionStrategy);
	const firstAction = fragments[0] || practice.title;

	if (/三件好事|三件趣事|好事|趣事|感恩|感谢|品味|美好|积极事件/u.test(text)) {
		return {
			steps: [
				"回想今天已经发生的三件具体小事",
				"每件事写清楚时间、地点、人物或一个细节",
				"为其中一件补一句原因：它为什么会发生，或为什么值得留下",
			],
			recordPrompt: "可以按三行写：第一件事和原因；第二件事和原因；第三件事和原因。",
		};
	}

	if (/悲伤|落泪|哀伤|难过|失去|告别/u.test(text)) {
		return {
			steps: [
				"选一个让你难过的具体事件，不需要写得完整",
				"对照五阶段，标出此刻最接近的状态：否认、愤怒、祈求、抑郁或接受",
				"允许自己用一种安全方式表达：写三句话、哭一会儿，或告诉可信任的人一句真实感受",
			],
			recordPrompt: "可以按三句写：发生了什么；我现在更像哪一阶段；我允许自己怎样照顾这份悲伤。",
		};
	}

	if (/呼吸|五感|正念|身体|扫描|冥想|此时此刻|着陆|感官/u.test(text)) {
		return {
			steps: [
				"停下来 60 秒，把注意力放回身体或环境",
				"依次记录一个看见、听见或感觉到的具体信号",
				"选一个让身体稍微放松的动作，再做 3 次呼吸",
			],
			recordPrompt:
				"可以按三句写：我注意到了什么身体或环境信号；它让我有什么变化；我做了哪个放松动作。",
		};
	}

	if (/优势|长处|擅长|最好|最佳|高光|成功|自我效能|VIA/u.test(text)) {
		return {
			steps: [
				"回想一件你做得比较顺的真实小事",
				"从这件事里圈出一个你用到的优势或做法",
				"写下明天可以在哪个小场景复用它",
			],
			recordPrompt: "可以按三句写：我做顺了什么；我用了什么优势；明天我打算把它用在哪里。",
		};
	}

	if (/未来|希望|目标|路径|计划|一小步|可能自我|愿望|成长型/u.test(text)) {
		return {
			steps: [
				"选一个你希望慢慢靠近的方向",
				"写出一幅更具体的未来画面，包含一个生活细节",
				"拆出今天能做的一小步，控制在 10 分钟以内",
			],
			recordPrompt: "可以按三句写：我想靠近的未来是什么；画面里有什么细节；今天我先做哪一步。",
		};
	}

	if (/关系|朋友|家人|同事|伴侣|倾听|回应|联结|分享|赞美|原谅|沟通|求助/u.test(text)) {
		return {
			steps: [
				"选一个你今天想认真对待的人",
				"做一个具体关系动作：发一句感谢、认真听完，或表达一个需要",
				"记录这次互动后，你对这段关系多看见了什么",
			],
			recordPrompt:
				"可以按三句写：我对谁做了什么；对方或我有什么变化；这段关系里我看见了什么需要。",
		};
	}

	if (/压力|困难|失败|挫折|担心|焦虑|愤怒|情绪|自我关怀|打气|安慰|复盘|接纳/u.test(text)) {
		return {
			steps: [
				"写下一个今天真实让你卡住的点",
				"把它拆成可控和暂时不可控两部分",
				"给自己一句支持，并选择一个可执行的下一步",
			],
			recordPrompt: "可以按三句写：我卡在哪里；哪些能控制、哪些先放下；我下一步做什么来支持自己。",
		};
	}

	if (/问卷|量表|测评|自查/u.test(text)) {
		return {
			steps: [
				"按直觉完成自查，不追求答案好看",
				"选出一个分数最高或最低的项目",
				"写下它今天对应的一个真实行为证据",
			],
			recordPrompt: "可以按三句写：我选中的项目是什么；今天有什么证据；我想保留或调整哪一点。",
		};
	}

	if (fragments.length >= 2) {
		return {
			steps: [firstAction, fragments[1], fallback.steps[2]],
			recordPrompt: fallback.recordPrompt,
		};
	}

	if (fragments.length === 1) {
		return {
			steps: [firstAction, fallback.steps[1], fallback.steps[2]],
			recordPrompt: fallback.recordPrompt,
		};
	}

	return fallback;
};

const parseStoredPracticeSteps = (value?: string) => {
	if (!value) return [];
	try {
		const parsed = JSON.parse(value) as unknown;
		if (!Array.isArray(parsed)) return [];
		return parsed.filter(
			(item): item is string => typeof item === "string" && item.trim().length > 0,
		);
	} catch {
		return [];
	}
};

const mapApiPractice = (practice: ApiPractice): Practice | null => {
	if (!isDimensionId(practice.dimensionId)) return null;
	const minutes = practice.estimatedMinutes || 3;
	const form = practice.formTag || "行动";
	const hasAssessment = /测评|量表|问卷|VIA/u.test(`${practice.title} ${practice.actionStrategy}`);
	const fallbackActionDesign = buildPracticeActionDesign(practice);
	const storedSteps = parseStoredPracticeSteps(practice.actionStepsJson);
	const actionDesign = {
		recordPrompt: practice.recordPrompt?.trim() || fallbackActionDesign.recordPrompt,
		steps: storedSteps.length ? storedSteps : fallbackActionDesign.steps,
	};

	return {
		id: practice.id,
		dim: practice.dimensionId,
		title: practice.title,
		sub: `${minutes} 分钟 · ${form}`,
		intro: cleanPracticeCopy(practice.actionStrategy),
		share: Boolean(practice.isShareable),
		course: {
			title: practice.theorySource ? `方法来源：${practice.theorySource}` : "练习方法说明",
			len: practice.originalTheme || "积极心理学练习",
		},
		assess: hasAssessment ? { title: `${practice.title}评估`, q: 6 } : null,
		source: {
			appSurface: practice.appSurface,
			formTag: practice.formTag,
			originalTheme: practice.originalTheme,
			socialTag: practice.socialTag,
			theorySource: practice.theorySource,
		},
		steps: actionDesign.steps,
		recordPrompt: actionDesign.recordPrompt,
	};
};

const mapApiPractices = (practices: ApiPractice[] | undefined) =>
	(practices ?? [])
		.map(mapApiPractice)
		.filter((practice): practice is Practice => Boolean(practice));

const BADGE_ICONS: Record<string, LucideIcon> = {
	"badge-check": BadgeCheck,
	"heart-hand": HandHeart,
	star: Star,
	sun: SunMedium,
	trophy: Trophy,
};

const BADGE_CATALOG: Badge[] = [
	{
		id: "badge-first",
		Icon: BadgeCheck,
		desc: "完成第一次练习",
		dim: "aware",
		got: false,
		name: "初次练习",
	},
	{
		id: "badge-week",
		Icon: SunMedium,
		desc: "累计完成 7 次真实行动证据",
		dim: "strength",
		got: false,
		name: "一周稳定练习",
	},
	{
		id: "badge-relation",
		Icon: HandHeart,
		desc: "完成一次关系分享或关系记录",
		dim: "relate",
		got: false,
		name: "一次真诚联结",
	},
	{
		id: "badge-hope",
		Icon: Star,
		desc: "完成 10 次希望行动",
		dim: "hope",
		got: false,
		name: "希望行动者",
	},
	{
		id: "badge-resil",
		Icon: Trophy,
		desc: "完成 12 次韧性调适练习",
		dim: "resil",
		got: false,
		name: "韧性练习章",
	},
];

const mapApiBadges = (badges: ApiBadge[] | undefined): Badge[] =>
	(badges ?? []).map((badge) => ({
		id: badge.id,
		Icon: BADGE_ICONS[badge.icon] ?? BadgeCheck,
		desc: badge.description,
		dim: isDimensionId(badge.dimensionId) ? badge.dimensionId : "aware",
		got: Boolean(badge.unlockedAt),
		name: badge.name,
	}));

const mergeApiBadges = (badges: ApiBadge[] | undefined): Badge[] => {
	const remote = mapApiBadges(badges);
	const remoteMap = new Map(remote.map((badge) => [badge.id, badge]));
	const catalogIds = new Set(BADGE_CATALOG.map((badge) => badge.id));
	return [
		...BADGE_CATALOG.map((badge) => remoteMap.get(badge.id) ?? badge),
		...remote.filter((badge) => !catalogIds.has(badge.id)),
	];
};

const mapApiTimeline = (items: ApiTimelineItem[] | undefined): TimelineItem[] =>
	(items ?? []).map((item) => ({
		id: item.id,
		authorName: item.authorName,
		dim: item.dimensionId && isDimensionId(item.dimensionId) ? item.dimensionId : null,
		exerciseId: item.exerciseId,
		feedbackScore: item.feedbackScore,
		friendName: item.friendName,
		friendTag: item.friendTag,
		image: item.image,
		kind: item.kind,
		reactions: item.reactions,
		targetGroupId: item.targetGroupId,
		targetGroupName: item.targetGroupName,
		text: item.text,
		time: item.time,
		title: item.title,
		visibility: item.visibility,
		voice: item.voice,
	}));

const mapApiRelations = (items: ApiRelationAsset[] | undefined): RelationAsset[] =>
	(items ?? []).map((item) => ({
		id: item.id,
		from: item.sourceTitle || "关系行动",
		name: item.name,
		sourceKind: item.sourceKind,
		sourceNoteId: item.sourceNoteId,
		sourceRecordId: item.sourceRecordId,
		tag: item.tag,
		time: "刚刚",
		visibility: item.visibility,
	}));

const mapApiPrograms = (items: ApiProgram[] | undefined): ProgramPlan[] =>
	(items ?? [])
		.filter((item) => isDimensionId(item.dimensionId))
		.map((item) => {
			const currentDay = Math.max(1, Math.min(item.totalDays, item.currentDay || 1));
			const nextDay = item.days.find((day) => day.dayNumber === currentDay) ?? item.days[0];
			return {
				id: item.id,
				blurb: item.description,
				currentDay,
				days: item.days.map((day) => ({
					day: day.dayNumber,
					exerciseId: day.exerciseId,
					focus: day.focusText,
					title: day.title,
				})),
				dim: item.dimensionId as DimensionId,
				done: item.completedDays,
				gains: ["成长证据", "关键词", item.rewardText || "周期总结"],
				nextExerciseId: nextDay?.exerciseId ?? undefined,
				per: "每天约 3 分钟",
				rewardText: item.rewardText,
				title: item.title,
				total: item.totalDays,
			};
		});

const mapApiOngoing = (items: ApiOngoingPractice[] | undefined): Ongoing[] =>
	(items ?? []).map((item) => ({
		id: item.id,
		checked: item.checked,
		days: item.days,
		practiceId: item.exerciseId,
		today: item.todayIndex,
		week: item.week.length === 7 ? item.week : [0, 0, 0, 0, 0, 0, 0],
	}));

const mapApiGroups = (items: ApiGroup[] | undefined): GrowthGroup[] =>
	(items ?? [])
		.filter((item) => item.dimensionId && isDimensionId(item.dimensionId))
		.map((item) => ({
			id: item.id,
			description: item.description,
			dim: item.dimensionId as DimensionId,
			joined: item.joined,
			memberCount: item.memberCount,
			name: item.name,
			sessionLabel: item.session?.scheduledLabel ?? "本周",
			sessionTitle: item.session?.title ?? "共同练习",
		}));

const buildEmptyReport = (): GrowthReport => ({
	focusDimension: "aware",
	headline: "报告还在等待证据",
	nextDecision: "完成几次真实练习后，这里会给出最近变化和下一步建议。",
	ranges: [
		{ days: 7, dimensions: [], total: 0 },
		{ days: 28, dimensions: [], total: 0 },
		{ days: 90, dimensions: [], total: 0 },
	],
});

const mapApiReport = (report: ApiGrowthReport | undefined): GrowthReport =>
	report && isDimensionId(report.focusDimension)
		? {
				focusDimension: report.focusDimension,
				headline: report.headline,
				nextDecision: report.nextDecision,
				ranges: report.ranges.map((range) => ({
					...range,
					dimensions: range.dimensions.flatMap((item) =>
						isDimensionId(item.dimensionId)
							? [{ count: item.count, dimensionId: item.dimensionId }]
							: [],
					),
				})),
			}
		: buildEmptyReport();

const buildPracticeMap = (practices: Practice[]) =>
	Object.fromEntries(practices.map((practice) => [practice.id, practice])) as Record<
		string,
		Practice
	>;

const estimateEvidenceScore = (payload: PracticePayload, keyword: string) => {
	const text = payload.text.trim();
	let score = 1;
	if (text.length >= 8) score += 1;
	if (text.length >= 24) score += 1;
	if (/[，。；、,.!?！？]/u.test(text) || /因为|所以|感受|发现|下一步|具体/u.test(text)) {
		score += 1;
	}
	if (keyword.trim()) score += 1;
	if (payload.mode === "image" && payload.image) score += 1;
	if (payload.mode === "voice" && (payload.voice ?? 0) >= 8) score += 1;
	return Math.max(1, Math.min(5, score));
};

const getApiError = (error: unknown) => {
	if (typeof error !== "object" || !error || !("code" in error)) return null;
	const candidate = error as { code?: unknown; message?: unknown };
	return {
		code: typeof candidate.code === "number" ? candidate.code : 500,
		message: typeof candidate.message === "string" ? candidate.message : "操作失败",
	};
};

export const Home: React.FC = () => {
	const isAuthCallback =
		typeof window !== "undefined" && window.location.pathname === "/auth/callback";
	const [tab, setTab] = useState<Tab>("home");
	const [flow, setFlow] = useState<Flow>(null);
	const [authSession, setAuthSession] = useState<AuthingSession | null>(() => loadAuthingSession());
	const [authLoading, setAuthLoading] = useState(false);
	const pendingAuthActionRef = useRef<(() => void | Promise<void>) | null>(null);
	const authRedirectStartedRef = useRef(false);
	const [selectedPractice, setSelectedPractice] = useState<Practice | null>(null);
	const [recordFor, setRecordFor] = useState<Practice | null>(null);
	const [timeline, setTimeline] = useState<TimelineItem[]>([]);
	const [groupTimeline, setGroupTimeline] = useState<TimelineItem[]>([]);
	const [ongoing, setOngoing] = useState<Ongoing[]>([]);
	const [openingLine, setOpeningLine] = useState("");
	const [todayPractice, setTodayPractice] = useState<Practice | null>(null);
	const [todayCompleted, setTodayCompleted] = useState(false);
	const [todayKeyword, setTodayKeyword] = useState("");
	const [toast, setToast] = useState("");
	const [success, setSuccess] = useState<SuccessState | null>(null);
	const [shareFor, setShareFor] = useState<TimelineItem | null>(null);
	const [relations, setRelations] = useState<RelationAsset[]>([]);
	const [badges, setBadges] = useState<Badge[]>(BADGE_CATALOG);
	const [groups, setGroups] = useState<GrowthGroup[]>([]);
	const [programs, setPrograms] = useState<ProgramPlan[]>([]);
	const {
		data: bootstrap,
		error: bootstrapError,
		isLoading: bootstrapLoading,
		mutate: refreshBootstrap,
	} = useSWR(authSession ? growthBootstrapKey : null, getGrowthBootstrap, {
		shouldRetryOnError: false,
	});
	const apiPractices = useMemo(() => mapApiPractices(bootstrap?.data.practices), [bootstrap]);
	const practices = apiPractices;
	const practiceMap = useMemo(() => buildPracticeMap(practices), [practices]);
	const practiceLoadError = bootstrapError
		? (getApiError(bootstrapError)?.message ?? "数据库题库加载失败，请检查后端接口")
		: null;
	const subskills = bootstrap?.data.subskills ?? [];
	const evidenceRules = bootstrap?.data.evidenceRules ?? [];
	const publicProfileName = getPublicProfileName(authSession);
	const profile: ProfileStats = {
		...USER_INFO,
		...bootstrap?.data.profile,
		avTxt: publicProfileName.slice(0, 1) || USER_INFO.avTxt,
		joined: authSession ? "已登录 · 成长数据已同步" : "登录后同步你的成长档案",
		mediaCount: bootstrap?.data.profile?.mediaCount ?? 0,
		name: publicProfileName,
		notesCount: bootstrap?.data.profile?.notesCount ?? 0,
		todayCompleted: bootstrap?.data.profile?.todayCompleted ?? 0,
	};
	const report = mapApiReport(bootstrap?.data.report);
	const activeProgram =
		programs.find((program) => program.done < program.total) ?? programs[0] ?? null;
	const programPractice = activeProgram
		? ((activeProgram.nextExerciseId ? practiceMap[activeProgram.nextExerciseId] : null) ??
			practices.find((practice) => practice.dim === activeProgram.dim) ??
			null)
		: null;
	const encouragementLine = bootstrap?.data.dailyEncouragement ?? "把现在写下来，练习会更贴近你";

	useEffect(() => {
		if (!bootstrap?.data) return;
		const nextTimeline = mapApiTimeline(bootstrap.data.timeline);
		setTimeline(nextTimeline);
		setGroupTimeline(mapApiTimeline(bootstrap.data.groupTimeline));
		const nextRelations = mapApiRelations(bootstrap.data.relations);
		setRelations(nextRelations);
		const nextBadges = mergeApiBadges(bootstrap.data.badges);
		setBadges(nextBadges);
		const nextGroups = mapApiGroups(bootstrap.data.groups);
		setGroups(nextGroups);
		const nextPrograms = mapApiPrograms(bootstrap.data.programs);
		setPrograms(nextPrograms);
		const nextOngoing = mapApiOngoing(bootstrap.data.ongoing);
		setOngoing(nextOngoing);
		if (bootstrap.data.todayDraw) {
			const drawnPractice = mapApiPractice(bootstrap.data.todayDraw.practice);
			if (drawnPractice) {
				setTodayPractice(drawnPractice);
			}
			setTodayCompleted(bootstrap.data.todayDraw.completed);
			setOpeningLine((current) => current || bootstrap.data.todayDraw?.openingLine || "");
		} else {
			setTodayPractice(null);
			setTodayCompleted(false);
		}
	}, [bootstrap]);

	useEffect(() => {
		if (!toast) return undefined;
		const timer = window.setTimeout(() => setToast(""), 1800);
		return () => window.clearTimeout(timer);
	}, [toast]);

	const showToast = useCallback((message: string) => setToast(message), []);

	const hasAuthSession = () => Boolean(authSession || loadAuthingSession());

	const startAuthingLogin = useCallback(async () => {
		if (authRedirectStartedRef.current) return;
		authRedirectStartedRef.current = true;
		setAuthLoading(true);
		showToast("正在打开 Authing 登录");
		try {
			const { redirectToAuthingLogin } = await import("@/lib/authing-client");
			redirectToAuthingLogin();
		} catch {
			authRedirectStartedRef.current = false;
			setAuthLoading(false);
			showToast("打开 Authing 登录失败，请稍后重试");
		}
	}, [showToast]);

	const requireAuth = (action?: () => void | Promise<void>) => {
		if (hasAuthSession()) return true;
		pendingAuthActionRef.current = action ?? null;
		void startAuthingLogin();
		return false;
	};

	const runProtected = (action: () => void | Promise<void>) => {
		if (requireAuth(action)) void action();
	};

	const confirmTodayKeyword = () => {
		const keyword = todayKeyword.trim();
		if (!keyword) {
			showToast("先留一个词");
			return;
		}
		setOpeningLine((current) => (current.trim() ? current : keyword));
		runProtected(() => setFlow("draw"));
	};

	const handleAuthLogout = async () => {
		pendingAuthActionRef.current = null;
		clearAuthingSession();
		setAuthSession(null);
		try {
			const { authingClient } = await import("@/lib/authing-client");
			await authingClient.logout();
		} catch {
			// Local session has already been cleared.
		}
		void refreshBootstrap();
		showToast("已退出登录");
	};

	useEffect(() => {
		if (window.location.pathname !== "/auth/callback") return;
		setAuthLoading(true);
		void import("@/lib/authing-client")
			.then(({ consumeAuthingCallback }) => consumeAuthingCallback())
			.then((session) => {
				if (!session) return;
				saveAuthingSession(session);
				setAuthSession(session);
				void refreshBootstrap();
				showToast("已登录，正在同步成长档案");
			})
			.catch((error: unknown) => {
				const message = error instanceof Error ? error.message : "Authing 登录失败";
				showToast(message);
				window.history.replaceState(null, "", "/");
			})
			.finally(() => setAuthLoading(false));
	}, [refreshBootstrap]);

	useEffect(() => {
		if (authSession || isAuthCallback) return;
		void startAuthingLogin();
	}, [authSession, isAuthCallback, startAuthingLogin]);

	useEffect(() => {
		if (!authSession?.token || authSession.username || authSession.name !== USER_INFO.name) return;
		let cancelled = false;
		void import("@/lib/authing-client")
			.then(({ refreshAuthingSession }) => refreshAuthingSession(authSession))
			.then((session) => {
				if (cancelled) return;
				setAuthSession(session);
			})
			.catch(() => {
				// Keep the existing local session if Authing userInfo is temporarily unavailable.
			});
		return () => {
			cancelled = true;
		};
	}, [authSession]);

	const openTab = (next: Tab) => {
		setTab(next);
		setFlow(null);
		setSelectedPractice(null);
		setRecordFor(null);
	};

	const openPractice = (practiceId?: string) => {
		if (!practiceId) {
			openTab("practice");
			return;
		}
		const practice = practiceMap[practiceId];
		if (!practice) {
			openTab("practice");
			return;
		}
		setSelectedPractice(practice);
		setFlow("detail");
	};

	const drawToday = async () => {
		if (todayPractice) return todayPractice;
		try {
			const response = await drawGrowthPractice({ availableMinutes: 3, openingLine });
			const remotePractice = mapApiPractice(response.data.practice);
			if (!remotePractice) throw new Error("今日练习数据异常");
			setTodayPractice(remotePractice);
			setTodayCompleted(response.data.completed);
			setOpeningLine((current) => current || response.data.todayDraw.openingLine);
			void refreshBootstrap();
			return remotePractice;
		} catch (error) {
			showToast(getApiError(error)?.message ?? "今日翻卡失败，请检查后端接口");
			throw error;
		}
	};

	const toggleOngoing = async (practiceId: string) => {
		if (!requireAuth(() => toggleOngoing(practiceId))) return;
		const wasActive = ongoing.some((item) => item.practiceId === practiceId);
		try {
			const response = await toggleOngoingPractice({ exerciseId: practiceId });
			await refreshBootstrap();
			showToast(response.data.active ? "已加入持续练习" : "已移出持续练习");
		} catch (error) {
			showToast(
				getApiError(error)?.message ??
					(wasActive ? "移出失败，请检查后端接口" : "加入失败，请检查后端接口"),
			);
		}
	};

	const completePractice = async (practice: Practice, payload: PracticePayload) => {
		if (todayCompleted && todayPractice?.id === practice.id) {
			showToast("今天这个练习已完成，可在记录里查看");
			return;
		}
		if (!requireAuth(() => completePractice(practice, payload))) return;
		const localId = `r${Date.now()}`;
		const localScore = estimateEvidenceScore(payload, todayKeyword);
		let recordId = localId;
		let evidenceScore = localScore;
		let nextSuggestion =
			localScore >= 5
				? "这次记录很具体，已经能清楚看到你的行动和感受。"
				: localScore <= 2
					? "这次记录还比较简短。多写一点做了什么、感受或原因，下一次推荐会更准。"
					: "这次记录已经可用。再补一点细节，成长档案会更清楚。";
		let nextAction = localScore >= 5 ? "upgrade" : localScore <= 2 ? "switch" : "continue";
		try {
			const response = await createPracticeRecord({
				content: payload.text,
				exerciseId: practice.id,
				imageKey: payload.image,
				mode: payload.mode,
				summaryKeyword: todayKeyword,
				voiceSeconds: payload.voice,
			});
			recordId = response.data.id;
			evidenceScore = response.data.evidenceScore;
			nextSuggestion = response.data.nextSuggestion;
			nextAction = response.data.nextAction;
			setBadges(mergeApiBadges(response.data.badges));
			if (response.data.programProgress) {
				setPrograms((items) =>
					items.map((item) =>
						item.id === response.data.programProgress?.programId
							? {
									...item,
									currentDay: response.data.programProgress.currentDay,
									done: response.data.programProgress.completedDays,
								}
							: item,
					),
				);
			}
		} catch (error) {
			showToast(getApiError(error)?.message ?? "练习保存失败，请检查后端接口");
			return;
		}
		const entry: TimelineItem = {
			id: recordId,
			kind: "practice",
			dim: practice.dim,
			exerciseId: practice.id,
			feedbackScore: evidenceScore,
			title: practice.title,
			time: "刚刚",
			text: payload.text || `完成了「${practice.title}」。今天先把这个动作做完，也算一次有效练习。`,
			image: payload.mode === "image" ? (payload.image ?? "paper") : null,
			voice: payload.mode === "voice" ? (payload.voice ?? 12) : 0,
		};
		setTimeline((items) => [entry, ...items]);
		setOngoing((items) => {
			const nextItems = items.map((item) =>
				item.practiceId === practice.id && !item.checked
					? {
							...item,
							checked: true,
							days: item.days + 1,
							week: item.week.map((value, index) => (index === item.today ? 1 : value)),
						}
					: item,
			);
			return nextItems;
		});
		if (todayPractice?.id === practice.id) {
			setTodayCompleted(true);
		}
		void refreshBootstrap();
		setRecordFor(null);
		setFlow(null);
		setSelectedPractice(null);
		setTab("home");
		setSuccess({ entry, evidenceScore, nextAction, nextSuggestion, practice, recordId });
	};

	const publishNote = async (payload: PracticePayload) => {
		if (!requireAuth(() => publishNote(payload))) return;
		let noteId = `n${Date.now()}`;
		const visibility = payload.visibility ?? "private";
		const targetGroup = groups.find((group) => group.id === payload.targetGroupId) ?? null;
		let relationAsset: RelationAsset | null = null;
		try {
			const response = await createNote({
				authorName: profile.name,
				content: payload.text,
				friendName: payload.friendName,
				friendTag: payload.friendTag,
				imageKey: payload.image,
				mode: payload.mode,
				targetGroupId: payload.targetGroupId,
				visibility,
				voiceSeconds: payload.voice,
			});
			noteId = response.data.id;
			if (response.data.relationAsset) {
				relationAsset = mapApiRelations([response.data.relationAsset])[0];
			}
			if (response.data.badges?.length) {
				setBadges(mergeApiBadges(response.data.badges));
			}
		} catch (error) {
			showToast(getApiError(error)?.message ?? "随心记保存失败，请检查后端接口");
			return;
		}
		const entry: TimelineItem = {
			id: noteId,
			kind: "note",
			authorName: profile.name,
			dim: null,
			friendName: visibility === "friend" ? payload.friendName : null,
			friendTag: visibility === "friend" ? payload.friendTag : null,
			time: "刚刚",
			text: payload.text || "留下了一条没有分类的随心记录。",
			image: payload.mode === "image" ? "paper" : null,
			reactions: visibility === "group" ? { seen: 0, support: 0 } : undefined,
			targetGroupId: visibility === "group" ? payload.targetGroupId : null,
			targetGroupName: visibility === "group" ? targetGroup?.name : null,
			visibility,
			voice: payload.mode === "voice" ? (payload.voice ?? 10) : 0,
		};
		setTimeline((items) => [entry, ...items]);
		if (visibility === "group") {
			setGroupTimeline((items) => [entry, ...items]);
		}
		if (visibility === "friend") {
			setRelations((items) => (relationAsset ? [relationAsset, ...items] : items));
		}
		setFlow(null);
		setTab("records");
		showToast(
			visibility === "group"
				? "已发到小组"
				: visibility === "friend"
					? "已生成朋友分享"
					: "随心记已保存",
		);
	};

	const addRelationAsset = async (asset: Omit<RelationAsset, "id" | "time" | "from">) => {
		if (!shareFor) return;
		if (!requireAuth(() => addRelationAsset(asset))) return;
		try {
			const response = await createRelationAsset({
				name: asset.name,
				sourceKind: "practice",
				sourceRecordId: shareFor.id,
				sourceTitle: shareFor.title ?? "随心记",
				tag: asset.tag,
				visibility: asset.visibility ?? "friend",
			});
			setRelations((items) => [mapApiRelations([response.data.asset])[0], ...items]);
			setBadges(mergeApiBadges(response.data.badges));
		} catch (error) {
			showToast(getApiError(error)?.message ?? "分享失败，请检查后端接口");
			return;
		}
		setTimeline((items) =>
			items.map((item) =>
				item.id === shareFor.id ? { ...item, friendName: asset.name, friendTag: asset.tag } : item,
			),
		);
		setShareFor(null);
		showToast(`已准备分享到 ${asset.name}`);
	};

	const submitFeedback = async (score: number, reason: string) => {
		if (!success?.recordId) return;
		if (!requireAuth(() => submitFeedback(score, reason))) return;
		try {
			const response = await createPracticeFeedback({
				exerciseId: success.practice.id,
				reason,
				recordId: success.recordId,
				score,
			});
			setSuccess((current) =>
				current
					? {
							...current,
							evidenceScore: response.data.score,
							nextAction: response.data.nextAction,
							nextSuggestion: response.data.nextSuggestion,
						}
					: current,
			);
			showToast("反馈已用于下一次推荐");
		} catch {
			showToast("反馈保存失败，请检查后端接口");
		}
	};

	const joinGroup = async (groupId: string) => {
		if (!requireAuth(() => joinGroup(groupId))) return;
		try {
			await joinGrowthGroup({ groupId });
			setGroups((items) =>
				items.map((group) => (group.id === groupId ? { ...group, joined: true } : group)),
			);
			showToast("已加入小组");
		} catch (error) {
			showToast(getApiError(error)?.message ?? "加入小组失败，请检查后端接口");
		}
	};

	const applyGroupReactions = (
		noteId: string,
		reactions: NonNullable<TimelineItem["reactions"]>,
	) => {
		const update = (items: TimelineItem[]) =>
			items.map((item) => (item.id === noteId ? { ...item, reactions } : item));
		setTimeline(update);
		setGroupTimeline(update);
	};

	const reactToGroupNote = async (noteId: string, reactionType: GroupReactionType) => {
		if (!requireAuth(() => reactToGroupNote(noteId, reactionType))) return;
		try {
			const response = await createGroupReaction({ noteId, reactionType });
			applyGroupReactions(noteId, response.data.reactions);
		} catch (error) {
			showToast(getApiError(error)?.message ?? "回应失败，请检查后端接口");
		}
	};

	if (!authSession || authLoading || isAuthCallback) {
		return (
			<main className="guanghe-page">
				<div className="phone-shell" aria-label="光合 App 原型">
					<div className="auth-gate-screen">
						<div className="auth-redirect-card">
							<strong>{isAuthCallback ? "正在完成登录" : "正在进入登录"}</strong>
							<span>登录后才能查看你的成长练习</span>
						</div>
					</div>
					{toast && <div className="gh-toast">{toast}</div>}
				</div>
			</main>
		);
	}

	return (
		<main className="guanghe-page">
			<div className="phone-shell" aria-label="光合 App 原型">
				<div className="phone-viewport">
					{tab === "home" && (
						<HomeScreen
							onDraw={() => runProtected(() => setFlow("draw"))}
							onGoPractice={openPractice}
							onKeywordConfirm={confirmTodayKeyword}
							onOpeningChange={setOpeningLine}
							onSummaryChange={setTodayKeyword}
							onViewRecords={() => {
								setFlow(null);
								setSelectedPractice(null);
								setTab("records");
							}}
							encouragementLine={encouragementLine}
							ongoing={ongoing}
							openingLine={openingLine}
							profile={profile}
							program={activeProgram}
							practiceMap={practiceMap}
							programPractice={programPractice}
							todayCompleted={todayCompleted}
							todayKeyword={todayKeyword}
							todayPractice={todayPractice}
						/>
					)}
					{tab === "practice" && (
						<PracticeScreen
							activeIds={ongoing.map((item) => item.practiceId)}
							isLoading={bootstrapLoading}
							loadError={practiceLoadError}
							onOpenPractice={openPractice}
							onToggleOngoing={(practiceId) => {
								void toggleOngoing(practiceId);
							}}
							practices={practices}
							subskills={subskills}
						/>
					)}
					{tab === "records" && (
						<RecordsScreen
							groupItems={groupTimeline}
							items={timeline}
							onCreateNote={() => runProtected(() => setFlow("note"))}
							onReact={reactToGroupNote}
						/>
					)}
					{tab === "mine" && (
						<MineScreen
							authSession={authSession}
							badges={badges}
							groups={groups}
							onAuthLogin={() => void startAuthingLogin()}
							onAuthLogout={handleAuthLogout}
							onJoinGroup={(groupId) => runProtected(() => joinGroup(groupId))}
							profile={profile}
							relations={relations}
							report={report}
							timeline={timeline}
						/>
					)}
				</div>
				<BottomNav
					active={tab}
					onNote={() => runProtected(() => setFlow("note"))}
					onTab={openTab}
				/>

				{flow === "draw" && (
					<DrawFlow
						keyword={openingLine}
						onClose={() => setFlow(null)}
						onDraw={drawToday}
						onGo={(practice) => {
							setSelectedPractice(practice);
							setFlow("detail");
						}}
						todayPractice={todayPractice}
					/>
				)}
				{flow === "detail" && selectedPractice && (
					<PracticeDetail
						onClose={() => {
							setFlow(null);
							setSelectedPractice(null);
						}}
						onComplete={completePractice}
						onRecord={(practice) => {
							runProtected(() => {
								setFlow(null);
								setRecordFor(practice);
							});
						}}
						onViewRecords={() => {
							setFlow(null);
							setSelectedPractice(null);
							setTab("records");
						}}
						evidenceRules={evidenceRules}
						isCompleted={todayCompleted && todayPractice?.id === selectedPractice.id}
						practice={selectedPractice}
						subskills={subskills}
					/>
				)}
				{recordFor && (
					<RecordSheet
						onClose={() => setRecordFor(null)}
						onDone={completePractice}
						practice={recordFor}
					/>
				)}
				{flow === "note" && (
					<NoteSheet groups={groups} onClose={() => setFlow(null)} onPublish={publishNote} />
				)}
				{success && (
					<SuccessOverlay
						entry={success.entry}
						onClose={() => setSuccess(null)}
						onHome={() => {
							setSuccess(null);
							setTab("home");
						}}
						onShare={() => {
							runProtected(() => {
								setShareFor(success.entry);
								setSuccess(null);
							});
						}}
						evidenceScore={success.evidenceScore}
						nextAction={success.nextAction}
						nextSuggestion={success.nextSuggestion}
						onFeedback={submitFeedback}
						practice={success.practice}
					/>
				)}
				{shareFor && (
					<ShareSheet
						entry={shareFor}
						onClose={() => setShareFor(null)}
						onShared={addRelationAsset}
					/>
				)}
				{authLoading && (
					<div className="auth-redirect-mask">
						<div className="auth-redirect-card">
							<strong>正在打开登录</strong>
							<span>登录后会回到光合练习</span>
						</div>
					</div>
				)}
				{toast && <div className="gh-toast">{toast}</div>}
			</div>
		</main>
	);
};

const DimChip: React.FC<{ dim: DimensionId; quiet?: boolean }> = ({ dim, quiet }) => {
	const dimension = DIM[dim];
	const Icon = dimension.Icon;
	return (
		<span
			className={cn("dim-chip", quiet && "quiet")}
			style={
				{
					"--dim": dimension.color,
					"--dim-soft": dimension.soft,
					"--dim-deep": dimension.deep,
				} as React.CSSProperties
			}
		>
			<Icon size={13} strokeWidth={2.4} />
			{dimension.name}
		</span>
	);
};

const Avatar: React.FC<{ label: string; size?: number; src?: string }> = ({
	label,
	size = 44,
	src,
}) => (
	<div className="avatar" style={{ height: size, width: size }}>
		{src ? <img alt="" src={src} /> : label}
	</div>
);

// 五维插画 / placeholder-art — abstract on-brand scenes that give every module imagery.
const DIM_MOTIF: Record<DimensionId, React.ReactNode> = {
	aware: (
		<>
			<circle cx="68" cy="30" r="14" fill="currentColor" opacity="0.9" />
			<g stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" opacity="0.55">
				<path d="M68 8v6" />
				<path d="M88 30h-6" />
				<path d="M83 15l-4 4" />
			</g>
			<path d="M0 64 Q26 46 50 60 T100 56 V84 H0 Z" fill="currentColor" opacity="0.16" />
			<path d="M0 74 Q30 60 56 72 T100 70 V84 H0 Z" fill="currentColor" opacity="0.34" />
			<path d="M24 40l2 5 5 2-5 2-2 5-2-5-5-2 5-2z" fill="currentColor" opacity="0.85" />
		</>
	),
	strength: (
		<>
			<circle cx="24" cy="24" r="9" fill="currentColor" opacity="0.85" />
			<path d="M0 84 L34 38 L56 70 L74 44 L100 84 Z" fill="currentColor" opacity="0.34" />
			<path d="M40 84 L74 44 L100 84 Z" fill="currentColor" opacity="0.18" />
			<path d="M74 44 v-16 l13 7 -13 6" fill="currentColor" opacity="0.9" />
		</>
	),
	relate: (
		<>
			<circle cx="38" cy="42" r="20" fill="currentColor" opacity="0.22" />
			<circle cx="64" cy="42" r="20" fill="currentColor" opacity="0.34" />
			<path
				d="M50 30c4-6 14-4 14 3 0 6-9 11-14 15-5-4-14-9-14-15 0-7 10-9 14-3z"
				fill="currentColor"
				opacity="0.9"
			/>
		</>
	),
	resil: (
		<>
			<path
				d="M6 78 H94"
				stroke="currentColor"
				strokeWidth="2.6"
				strokeLinecap="round"
				opacity="0.3"
			/>
			<path
				d="M50 78 V44"
				stroke="currentColor"
				strokeWidth="3.4"
				strokeLinecap="round"
				opacity="0.85"
			/>
			<path d="M50 52 C36 50 30 40 30 30 c12 0 20 8 20 22z" fill="currentColor" opacity="0.5" />
			<path d="M50 46 C64 44 72 33 72 22 c-12 0-22 9-22 24z" fill="currentColor" opacity="0.85" />
		</>
	),
	hope: (
		<>
			<path
				d="M8 70 Q40 64 60 40 T96 16"
				stroke="currentColor"
				strokeWidth="2.4"
				strokeDasharray="2 7"
				strokeLinecap="round"
				opacity="0.5"
				fill="none"
			/>
			<path d="M58 30 L92 14 L80 50 L70 38 Z" fill="currentColor" opacity="0.9" />
			<path d="M70 38 L80 50 L70 52 Z" fill="currentColor" opacity="0.55" />
			<path
				d="M20 26l1.6 4 4 1.6-4 1.6-1.6 4-1.6-4-4-1.6 4-1.6z"
				fill="currentColor"
				opacity="0.8"
			/>
		</>
	),
};

const DimScene: React.FC<{ dim: DimensionId; className?: string }> = ({ dim, className }) => (
	<div aria-hidden="true" className={cn("dim-scene", className)} style={{ color: DIM[dim].color }}>
		<svg fill="none" viewBox="0 0 100 84" xmlns="http://www.w3.org/2000/svg">
			{DIM_MOTIF[dim]}
		</svg>
	</div>
);

// Compact framed art used inside list rows — a soft tile with the scene + dimension hue.
const DimArt: React.FC<{ dim: DimensionId; size?: number }> = ({ dim, size = 56 }) => (
	<div
		className="dim-art"
		style={
			{
				"--art": DIM[dim].color,
				"--art-soft": DIM[dim].soft,
				height: size,
				width: size,
			} as React.CSSProperties
		}
	>
		<DimScene dim={dim} />
	</div>
);

const BottomNav: React.FC<{ active: Tab; onNote: () => void; onTab: (tab: Tab) => void }> = ({
	active,
	onNote,
	onTab,
}) => {
	const tabs: Array<{ id: Tab; label: string; Icon: LucideIcon }> = [
		{ id: "home", label: "首页", Icon: HomeIcon },
		{ id: "practice", label: "练习", Icon: LibraryBig },
		{ id: "records", label: "记录", Icon: PenLine },
		{ id: "mine", label: "我的", Icon: User },
	];
	return (
		<nav aria-label="主导航" className="bottom-nav">
			<div className="bottom-nav-row">
				{tabs.map(({ Icon, id, label }, index) => (
					<button
						aria-label={label}
						className={cn("bottom-tab", active === id && "active", index === 1 && "nav-gap")}
						key={id}
						onClick={() => onTab(id)}
						type="button"
					>
						<span className="bottom-tab-dot" />
						<Icon size={23} strokeWidth={active === id ? 2.6 : 2.2} />
						<span className="bottom-tab-label">{label}</span>
					</button>
				))}
			</div>
			<button aria-label="随心记" className="note-fab" onClick={onNote} type="button">
				<Plus size={24} strokeWidth={2.6} />
			</button>
		</nav>
	);
};

const HomeScreen: React.FC<{
	encouragementLine: string;
	ongoing: Ongoing[];
	openingLine: string;
	profile: ProfileStats;
	program: ProgramPlan | null;
	practiceMap: Record<string, Practice>;
	programPractice: Practice | null;
	todayCompleted: boolean;
	todayKeyword: string;
	todayPractice: Practice | null;
	onDraw: () => void | Promise<Practice>;
	onGoPractice: (id?: string) => void;
	onKeywordConfirm: () => void;
	onOpeningChange: (value: string) => void;
	onSummaryChange: (value: string) => void;
	onViewRecords: () => void;
}> = ({
	encouragementLine,
	ongoing,
	openingLine,
	onDraw,
	onGoPractice,
	onKeywordConfirm,
	onOpeningChange,
	onSummaryChange,
	onViewRecords,
	profile,
	program,
	practiceMap,
	programPractice,
	todayCompleted,
	todayKeyword,
	todayPractice,
}) => (
	<section className="app-screen home-screen">
		<header className="home-head">
			<div className="home-head-top">
				<span>{encouragementLine}</span>
				<div className="home-head-side">
					<span className="wellbeing-pill" title="幸福力">
						<Sparkles size={13} />
						{profile.wellbeing}
					</span>
					<button aria-label="练习提醒" className="icon-button" type="button">
						<Bell size={18} />
					</button>
				</div>
			</div>
			<h1>今天，从哪里开始？</h1>
		</header>

		<form
			className="opening-note"
			aria-label="今日开场"
			onSubmit={(event) => {
				event.preventDefault();
				if (openingLine.trim()) void onDraw();
			}}
		>
			<label htmlFor="opening-line">今日开场</label>
			<div className="opening-note-row">
				<input
					id="opening-line"
					onChange={(event) => onOpeningChange(event.target.value)}
					placeholder="一句话描述此刻状态，可跳过"
					value={openingLine}
				/>
				<button className="opening-confirm" disabled={!openingLine.trim()} type="submit">
					确定
				</button>
			</div>
		</form>

		<TodayPracticeCard
			completed={todayCompleted}
			onDraw={onDraw}
			onGoPractice={onGoPractice}
			onViewRecords={onViewRecords}
			practice={todayPractice}
		/>

		{program && programPractice && (
			<ProgramCard onGoPractice={onGoPractice} practice={programPractice} program={program} />
		)}

		<section className="section-block">
			<div className="section-title">
				<div>
					<span>持续训练</span>
					<h2>持续中的训练</h2>
				</div>
				<button className="text-link" onClick={() => onGoPractice()} type="button">
					去添加
					<ArrowRight size={15} />
				</button>
			</div>
			<div className="ongoing-list">
				{ongoing.length ? (
					ongoing.map((item) => (
						<OngoingCard
							item={item}
							key={item.id}
							onGoPractice={onGoPractice}
							practiceMap={practiceMap}
						/>
					))
				) : (
					<OngoingEmpty onAdd={() => onGoPractice()} />
				)}
			</div>
		</section>

		<KeywordCard
			completed={todayCompleted}
			onChange={onSummaryChange}
			onConfirm={onKeywordConfirm}
			value={todayKeyword}
		/>
	</section>
);

const ProgramCard: React.FC<{
	practice: Practice;
	program: ProgramPlan;
	onGoPractice: (id?: string) => void;
}> = ({ onGoPractice, practice, program }) => {
	const dimension = DIM[program.dim];
	const percent = Math.round((program.done / program.total) * 100);
	const today = program.days.find((day) => day.day === program.currentDay);
	return (
		<section className="section-block">
			<div className="section-title">
				<div>
					<span>持续练习 · 专项计划</span>
					<h2>正在进行的成长目标</h2>
				</div>
			</div>
			<article
				className="program-card"
				style={
					{
						"--active-dim": dimension.color,
						"--active-dim-soft": dimension.soft,
						"--active-dim-deep": dimension.deep,
					} as React.CSSProperties
				}
			>
				<div className="program-art">
					<DimScene dim={program.dim} />
					<span className="program-ring" style={{ "--pct": `${percent}%` } as React.CSSProperties}>
						<em>
							{program.done}
							<i>/{program.total}</i>
						</em>
					</span>
				</div>
				<div className="program-body">
					<DimChip dim={program.dim} />
					<h3>{program.title}</h3>
					<p>{program.blurb}</p>
					<div className="program-gains">
						{program.gains.map((gain) => (
							<span key={gain}>{gain}</span>
						))}
					</div>
					{today && (
						<div className="program-today">
							<span>第 {today.day} 天</span>
							<strong>{today.title}</strong>
							<p>{today.focus}</p>
						</div>
					)}
					<div className="program-foot">
						<span className="program-per">{program.per}</span>
						<button
							className="primary-action compact"
							onClick={() => onGoPractice(practice.id)}
							type="button"
						>
							继续第 {program.currentDay} 天
							<ArrowRight size={15} />
						</button>
					</div>
				</div>
			</article>
		</section>
	);
};

const KeywordCard: React.FC<{
	completed: boolean;
	value: string;
	onChange: (value: string) => void;
	onConfirm: () => void;
}> = ({ completed, onChange, onConfirm, value }) => (
	<section className="section-block keyword-section">
		<article className="keyword-card">
			<div className="keyword-card-head">
				<div className="keyword-heading">
					<span>今日关键词</span>
					<h2>给今天留个词</h2>
				</div>
				<span className="keyword-preview">{value.trim() || "今天"}</span>
			</div>
			<form
				aria-label="确认今日关键词"
				className="keyword-input-row"
				onSubmit={(event) => {
					event.preventDefault();
					onConfirm();
				}}
			>
				<input
					onChange={(event) => onChange(event.target.value)}
					placeholder={completed ? "给今天的练习一个词…" : "先留下一个词…"}
					value={value}
				/>
				<button className="keyword-confirm" disabled={!value.trim()} type="submit">
					确定
				</button>
			</form>
			<div className="summary-tags keyword-tags">
				{SUMMARY_WORDS.map((word) => (
					<button
						className={cn(value === word && "active")}
						key={word}
						onClick={() => onChange(word)}
						type="button"
					>
						{word}
					</button>
				))}
			</div>
		</article>
	</section>
);

const TodayPracticeCard: React.FC<{
	completed: boolean;
	practice: Practice | null;
	onDraw: () => void | Promise<Practice>;
	onGoPractice: (id: string) => void;
	onViewRecords: () => void;
}> = ({ completed, onDraw, onGoPractice, onViewRecords, practice }) => {
	if (!practice) {
		return (
			<button
				aria-label="翻开今日练习卡"
				className="today-card draw-entry-card"
				onClick={() => void onDraw()}
				type="button"
			>
				<div className="draw-entry-copy">
					<div className="today-card-kicker">今日翻卡</div>
					<h2>翻开今天的练习卡</h2>
					<p className="today-empty-hint">进入独立抽卡页，随机获得一个真实小行动。</p>
					<span className="draw-entry-action">
						开始翻卡
						<ArrowRight size={16} />
					</span>
				</div>
				<div className="draw-entry-visual" aria-hidden="true">
					<div className="draw-entry-card-face">
						<strong>?</strong>
						<span>PERMA</span>
					</div>
					<i className="entry-dot dot-p">P</i>
					<i className="entry-dot dot-e">E</i>
					<i className="entry-dot dot-a">A</i>
				</div>
			</button>
		);
	}
	const dimension = DIM[practice.dim];
	return (
		<section
			className={cn("today-card", completed && "completed")}
			style={
				{
					"--active-dim": dimension.color,
					"--active-dim-soft": dimension.soft,
					"--active-dim-deep": dimension.deep,
				} as React.CSSProperties
			}
		>
			<div className="today-art" aria-hidden="true">
				<DimScene dim={practice.dim} />
			</div>
			<div className="today-status">
				<DimChip dim={practice.dim} />
				<span>{completed ? "已完成" : "待练习"}</span>
			</div>
			<h2>{practice.title}</h2>
			<p>{practice.intro}</p>
			<div className="today-steps">
				{practice.steps.map((step, index) => (
					<div className="today-step" key={step}>
						<span>{index + 1}</span>
						<p>{step}</p>
					</div>
				))}
			</div>
			<button
				className="primary-action"
				onClick={() => (completed ? onViewRecords() : onGoPractice(practice.id))}
				type="button"
			>
				{completed ? "查看完成记录" : "去练习"}
				<ArrowRight size={16} />
			</button>
		</section>
	);
};

const OngoingEmpty: React.FC<{ onAdd: () => void }> = ({ onAdd }) => (
	<EmptyState
		actionLabel="添加训练"
		icon={LibraryBig}
		onAction={onAdd}
		text="从练习库加入一个想稳定练习的动作，会保存到当前账号。"
		title="还没有持续训练"
		tone="strength"
	/>
);

const EmptyState: React.FC<{
	icon: LucideIcon;
	text: string;
	title: string;
	actionLabel?: string;
	className?: string;
	tone?: DimensionId;
	onAction?: () => void;
}> = ({ actionLabel, className, icon: Icon, onAction, text, title, tone = "aware" }) => {
	const dimension = DIM[tone];
	return (
		<article
			className={cn("friendly-empty", className)}
			style={
				{
					"--empty-dim": dimension.color,
					"--empty-soft": dimension.soft,
					"--empty-deep": dimension.deep,
				} as React.CSSProperties
			}
		>
			<div className="friendly-empty-icon">
				<Icon size={20} strokeWidth={2.2} />
			</div>
			<div className="friendly-empty-copy">
				<strong>{title}</strong>
				<p>{text}</p>
			</div>
			{actionLabel && onAction && (
				<button className="friendly-empty-action" onClick={onAction} type="button">
					{actionLabel}
					<ArrowRight size={13} />
				</button>
			)}
		</article>
	);
};

const OngoingCard: React.FC<{
	item: Ongoing;
	practiceMap: Record<string, Practice>;
	onGoPractice: (id: string) => void;
}> = ({ item, onGoPractice, practiceMap }) => {
	const practice = practiceMap[item.practiceId];
	if (!practice) return null;
	const dimension = DIM[practice.dim];
	const completedCount = item.week.reduce((sum, value) => sum + value, 0);
	const stage = item.days >= 12 ? "稳定练习中" : item.days >= 5 ? "正在形成节奏" : "刚开始建立";
	return (
		<article
			className="ongoing-card"
			style={{ "--dim": dimension.color, "--dim-soft": dimension.soft } as React.CSSProperties}
		>
			<div className="ongoing-top">
				<DimArt dim={practice.dim} size={48} />
				<div className="ongoing-copy">
					<strong>{practice.title}</strong>
					<span>
						{stage} · 最近完成 {completedCount} 次动作
					</span>
				</div>
			</div>
			<div className="training-state">
				<div>
					<span>下一步</span>
					<p>{practice.steps[0]}</p>
				</div>
				<span className={cn("state-pill", item.checked && "done")}>
					{item.checked ? "状态已更新" : "待更新"}
				</span>
			</div>
			<div className="ongoing-actions">
				<button
					className="primary-action ongoing-practice-action"
					onClick={() => onGoPractice(practice.id)}
					type="button"
				>
					去练习
					<ArrowRight size={14} />
				</button>
			</div>
		</article>
	);
};

const PracticeScreen: React.FC<{
	activeIds: string[];
	isLoading: boolean;
	loadError: string | null;
	practices: Practice[];
	subskills: ApiSubskill[];
	onOpenPractice: (id: string) => void;
	onToggleOngoing: (id: string) => void;
}> = ({
	activeIds,
	isLoading,
	loadError,
	onOpenPractice,
	onToggleOngoing,
	practices,
	subskills,
}) => {
	const emptyTitle = isLoading
		? "正在从数据库加载题库"
		: loadError
			? "数据库题库没有加载成功"
			: "数据库题库还是空的";
	const emptyText = isLoading
		? "题库来自 D1 的 exercises 表，加载完成后会按五类展示。"
		: loadError
			? loadError
			: "请先执行本地 D1 初始化和 seed：pnpm run db:init:local。";

	return (
		<section className="app-screen practice-screen">
			<header className="plain-header">
				<div>
					<span>练习</span>
					<h1>五类积极心理训练</h1>
				</div>
				<BookOpen size={24} />
			</header>
			<section className="model-card">
				<div>
					<strong>五维积极成长模型</strong>
					<p>觉察、优势、联结、韧性、希望，一天一小步，看见自己的变化。</p>
				</div>
				<span>加入后进入持续训练</span>
			</section>
			{practices.length === 0 ? (
				<EmptyState
					className="practice-bank-empty"
					icon={BookOpen}
					text={emptyText}
					title={emptyTitle}
					tone="strength"
				/>
			) : (
				<div className="practice-sections">
					{DIMENSIONS.map((dimension) => {
						const dimensionPractices = practices.filter(
							(practice) => practice.dim === dimension.id,
						);
						const dimensionSubskills = subskills.filter(
							(item) => item.dimensionId === dimension.id,
						);
						return (
							<section className="practice-group" key={dimension.id}>
								<div
									className="practice-group-head"
									style={
										{
											"--group-color": dimension.color,
											"--group-soft": dimension.soft,
											"--group-deep": dimension.deep,
										} as React.CSSProperties
									}
								>
									<div className="practice-group-copy">
										<h2>{dimension.name}</h2>
										<p>{dimension.blurb}</p>
									</div>
									<DimScene dim={dimension.id} className="practice-group-bg" />
								</div>
								{dimensionSubskills.length > 0 && (
									<div className="subskill-strip">
										{dimensionSubskills.map((item) => (
											<span key={item.id}>{item.name}</span>
										))}
									</div>
								)}
								<div className="practice-list">
									{dimensionPractices.map((practice) => {
										const active = activeIds.includes(practice.id);
										return (
											<article className="practice-card" key={practice.id}>
												<div className="practice-card-body">
													<h3>{practice.title}</h3>
													<span>{practice.sub}</span>
													<p>{practice.intro}</p>
													<div className="practice-tags">
														<span className="practice-tag">
															<BookOpen size={12} />
															课程
														</span>
														{practice.assess && (
															<span className="practice-tag">
																<ClipboardList size={12} />
																量表
															</span>
														)}
														{practice.share && (
															<span className="practice-tag">
																<Send size={12} />
																可分享
															</span>
														)}
													</div>
													<div className="practice-card-actions">
														<button
															className="start-practice-link"
															onClick={() => onOpenPractice(practice.id)}
															type="button"
														>
															开始练习
															<ArrowRight size={14} />
														</button>
														<button
															aria-pressed={active}
															className={cn("ongoing-toggle", active && "active")}
															onClick={() => onToggleOngoing(practice.id)}
															type="button"
														>
															{active ? <Check size={14} /> : <Plus size={14} />}
															{active ? "已在持续练习" : "加入持续练习"}
														</button>
													</div>
												</div>
											</article>
										);
									})}
								</div>
							</section>
						);
					})}
				</div>
			)}
		</section>
	);
};

const RECORD_FILTERS: Array<{ id: RecordFilter; label: string }> = [
	{ id: "all", label: "全部" },
	{ id: "practice", label: "练习" },
	{ id: "note", label: "随心记" },
	{ id: "group", label: "小组" },
	{ id: "friend", label: "好友分享" },
];

const RecordsScreen: React.FC<{
	groupItems: TimelineItem[];
	items: TimelineItem[];
	onCreateNote: () => void;
	onReact: (noteId: string, reactionType: GroupReactionType) => void;
}> = ({ groupItems, items, onCreateNote, onReact }) => {
	const [filter, setFilter] = useState<RecordFilter>("all");
	const practiceCount = items.filter((item) => item.kind === "practice").length;
	const noteCount = items.filter((item) => item.kind === "note").length;
	const mediaCount = items.filter((item) => item.image || item.voice > 0).length;
	const friendCount = items.filter(
		(item) => item.visibility === "friend" || item.friendName,
	).length;
	const filterCounts: Record<RecordFilter, number> = {
		all: items.length,
		friend: friendCount,
		group: groupItems.length,
		note: noteCount,
		practice: practiceCount,
	};
	const visibleItems =
		filter === "group"
			? groupItems
			: filter === "practice"
				? items.filter((item) => item.kind === "practice")
				: filter === "note"
					? items.filter((item) => item.kind === "note")
					: filter === "friend"
						? items.filter((item) => item.visibility === "friend" || item.friendName)
						: items;
	const emptyText =
		filter === "group"
			? "加入小组并发布随心记后，这里会出现小组成员的真实记录。"
			: filter === "friend"
				? "分享练习或随心记给朋友后，这里会保留你的分享证据。"
				: "完成练习或写下随心记后，时间轴会按时间保存你的真实变化。";
	return (
		<section className="app-screen records-screen">
			<header className="records-head">
				<div className="records-head-row">
					<div className="records-title">
						<span>记录</span>
						<h1>个人时间轴</h1>
					</div>
					<button
						aria-label="写一条随心记"
						className="records-compose"
						onClick={onCreateNote}
						type="button"
					>
						<PenLine size={18} />
					</button>
				</div>
				<div className="records-summary">
					<span>
						<strong>{practiceCount}</strong>
						练习记录
					</span>
					<span>
						<strong>{noteCount}</strong>
						随心记
					</span>
					<span>
						<strong>{mediaCount}</strong>
						语音/图片
					</span>
				</div>
				<div className="records-filter" role="tablist" aria-label="记录筛选">
					{RECORD_FILTERS.map((item) => (
						<button
							aria-selected={filter === item.id}
							className={cn(filter === item.id && "active")}
							key={item.id}
							onClick={() => setFilter(item.id)}
							role="tab"
							type="button"
						>
							{item.label}
							<span>{filterCounts[item.id]}</span>
						</button>
					))}
				</div>
			</header>
			<div className={cn("timeline-list", !visibleItems.length && "is-empty")}>
				{visibleItems.length ? (
					visibleItems.map((item) => (
						<TimelineCard item={item} key={`${filter}-${item.id}`} onReact={onReact} />
					))
				) : (
					<EmptyState
						actionLabel="写一条"
						className="timeline-empty"
						icon={PenLine}
						onAction={onCreateNote}
						text={emptyText}
						title="还没有时间轴记录"
						tone="hope"
					/>
				)}
			</div>
		</section>
	);
};

const TimelineCard: React.FC<{
	item: TimelineItem;
	onReact?: (noteId: string, reactionType: GroupReactionType) => void;
}> = ({ item, onReact }) => {
	const dimension = item.dim ? DIM[item.dim] : null;
	const isGroupNote = item.kind === "note" && item.visibility === "group";
	const isFriendShared = item.visibility === "friend" || item.friendName;
	return (
		<article
			className="timeline-card"
			style={{ "--pin": dimension ? dimension.color : "" } as React.CSSProperties}
		>
			<div className="timeline-pin" />
			<div className="timeline-body">
				<div className="timeline-meta">
					<span>{item.time}</span>
					<div className="timeline-tags">
						{dimension ? (
							<DimChip dim={dimension.id} quiet />
						) : (
							<span className="note-tag">
								{isGroupNote
									? `小组 · ${item.targetGroupName ?? "练习小组"}`
									: isFriendShared
										? `好友 · ${item.friendName ?? "朋友"}`
										: "随心记 · 仅自己"}
							</span>
						)}
						{dimension && item.friendName && (
							<span className="share-tag">已分享给 {item.friendName}</span>
						)}
					</div>
				</div>
				{item.title ? (
					<h2>{item.title}</h2>
				) : isGroupNote ? (
					<h2>{item.authorName ?? "组员"}的随心记</h2>
				) : null}
				<p>{item.text}</p>
				{item.feedbackScore && (
					<div className="evidence-score">
						<span>记录具体度</span>
						<strong>{item.feedbackScore}/5</strong>
					</div>
				)}
				{item.image && <div className="timeline-image" />}
				{item.voice > 0 && <VoiceBar seconds={item.voice} />}
				{isGroupNote && onReact && (
					<div className="group-reactions">
						<button onClick={() => onReact(item.id, "seen")} type="button">
							我看见了 <span>{item.reactions?.seen ?? 0}</span>
						</button>
						<button onClick={() => onReact(item.id, "support")} type="button">
							支持你 <span>{item.reactions?.support ?? 0}</span>
						</button>
					</div>
				)}
			</div>
		</article>
	);
};

const VoiceBar: React.FC<{ seconds: number }> = ({ seconds }) => (
	<div className="voice-bar">
		<span>
			<Play fill="currentColor" size={12} />
		</span>
		<div className="voice-waves">
			{Array.from({ length: 18 }).map((_, index) => (
				<i key={index} style={{ height: 5 + Math.abs(Math.sin(index * 1.3)) * 17 }} />
			))}
		</div>
		<em>{seconds}"</em>
	</div>
);

const DimensionPentagon: React.FC<{ counts: Record<DimensionId, number> }> = ({ counts }) => {
	const realTotal = DIMENSIONS.reduce((sum, dimension) => sum + counts[dimension.id], 0);
	const metrics = GROWTH_LEDGER.map((row) => ({
		...row,
		color: DIM[row.dim].color,
		count: counts[row.dim],
		deep: DIM[row.dim].deep,
		short: DIM[row.dim].short,
	}));
	const total = metrics.reduce((sum, item) => sum + item.count, 0);
	const maxCount = 10;
	const strongest = metrics.reduce(
		(best, item) => (item.count > best.count ? item : best),
		metrics[0],
	);
	const center = 110;
	const radius = 78;
	const labelRadius = 101;
	const angleOf = (index: number) => -Math.PI / 2 + (index * Math.PI * 2) / metrics.length;
	const point = (index: number, pointRadius: number) => {
		const angle = angleOf(index);
		return {
			x: center + Math.cos(angle) * pointRadius,
			y: center + Math.sin(angle) * pointRadius,
		};
	};
	const polygonPoints = (scale: number) =>
		metrics
			.map((_, index) => {
				const current = point(index, radius * scale);
				return `${current.x},${current.y}`;
			})
			.join(" ");
	const valuePoints = metrics
		.map((item, index) => {
			const scale = Math.min(1, item.count / maxCount);
			const current = point(index, radius * scale);
			return `${current.x},${current.y}`;
		})
		.join(" ");

	return (
		<article className={cn("dimension-pentagon-card", realTotal === 0 && "is-empty")}>
			<div className="pentagon-visual" aria-label="五维行动雷达图" role="img">
				<svg viewBox="0 0 220 220">
					{[0.2, 0.4, 0.6, 0.8, 1].map((scale) => (
						<polygon className="pentagon-grid" key={scale} points={polygonPoints(scale)} />
					))}
					{metrics.map((item, index) => {
						const outer = point(index, radius);
						return (
							<line
								className="pentagon-axis"
								key={item.dim}
								x1={center}
								x2={outer.x}
								y1={center}
								y2={outer.y}
							/>
						);
					})}
					<polygon className="pentagon-value" points={valuePoints} />
					{metrics.map((item, index) => {
						const outer = point(index, radius);
						const label = point(index, labelRadius);
						const abilityScale = Math.min(1, item.count / maxCount);
						const abilityPoint = point(index, radius * abilityScale);
						return (
							<g key={item.dim}>
								<circle className="pentagon-outer-dot" cx={outer.x} cy={outer.y} r="2.9" />
								<text
									className="pentagon-label"
									dominantBaseline="middle"
									textAnchor="middle"
									x={label.x}
									y={label.y}
								>
									{item.short}
								</text>
								<circle
									className="pentagon-value-point"
									cx={abilityPoint.x}
									cy={abilityPoint.y}
									r="4.6"
								/>
							</g>
						);
					})}
				</svg>
			</div>
			<div className="pentagon-copy">
				<span>{`累计 ${total} 次行动`}</span>
				<strong>{realTotal === 0 ? "等待第一条行动" : `${strongest.short}最明显`}</strong>
				<p>
					{realTotal === 0
						? "完成任意练习后，五个方向会按真实记录逐步点亮。"
						: `目前「${strongest.label}」最多，继续完成练习会让五边形更饱满。`}
				</p>
			</div>
			<div className="pentagon-legend">
				{metrics.map((item) => (
					<span key={item.dim} style={{ "--legend-color": item.color } as React.CSSProperties}>
						<i />
						<b>
							{item.count}
							{item.unit}
						</b>
						{item.label}
					</span>
				))}
			</div>
		</article>
	);
};

const MineScreen: React.FC<{
	authSession: AuthingSession | null;
	badges: Badge[];
	groups: GrowthGroup[];
	profile: ProfileStats;
	relations: RelationAsset[];
	report: GrowthReport;
	timeline: TimelineItem[];
	onAuthLogin: () => void;
	onAuthLogout: () => void;
	onJoinGroup: (groupId: string) => void;
}> = ({
	authSession,
	badges,
	groups,
	onAuthLogin,
	onAuthLogout,
	onJoinGroup,
	profile,
	relations,
	report,
	timeline,
}) => {
	const counts = DIMENSIONS.reduce(
		(acc, dimension) => {
			const count = timeline.filter((item) => item.dim === dimension.id).length;
			acc[dimension.id] = count;
			return acc;
		},
		{} as Record<DimensionId, number>,
	);
	const totalPracticeCount =
		profile.totalPractice || timeline.filter((item) => item.kind === "practice").length;
	const hasReportData = report.ranges.some((range) => range.total > 0);
	return (
		<section className="app-screen mine-screen">
			<div className="mine-hero">
				<div className="mine-hero-art" aria-hidden="true">
					<DimScene dim="hope" />
				</div>
				<div className="mine-head">
					<Avatar label={profile.avTxt} size={38} src={authSession?.avatar} />
					<div className="mine-id">
						<h1>{profile.name}</h1>
						<span>{profile.joined}</span>
					</div>
					<button
						aria-label={authSession ? "退出登录" : "登录"}
						className="mine-auth-button"
						onClick={authSession ? onAuthLogout : onAuthLogin}
						type="button"
					>
						{authSession ? "退出" : "登录"}
					</button>
				</div>
				<div className="wellbeing-block">
					<span className="mine-metric is-primary">
						<b>{profile.wellbeing}</b>
						<em>幸福力</em>
					</span>
					<span className="mine-metric">
						<b>{profile.steadyDays}</b>
						<em>持续天数</em>
					</span>
					<span className="mine-metric">
						<b>{totalPracticeCount}</b>
						<em>练习次数</em>
					</span>
					<span className="mine-metric">
						<b>{profile.todayCompleted}</b>
						<em>今日完成</em>
					</span>
				</div>
			</div>

			<section className="section-block">
				<div className="section-title compact">
					<div>
						<span>成长档案</span>
						<h2>五维行动雷达</h2>
					</div>
				</div>
				<DimensionPentagon counts={counts} />
			</section>

			<section className="section-block">
				<div className="section-title compact">
					<div>
						<span>周期报告</span>
						<h2>最近变化</h2>
					</div>
				</div>
				<article className={cn("report-card", !hasReportData && "is-empty")}>
					<DimChip dim={hasReportData ? report.focusDimension : "aware"} />
					<h3>{hasReportData ? report.headline : "报告还在等待证据"}</h3>
					<p>
						{hasReportData
							? report.nextDecision
							: "先留下几次练习记录，系统再帮你看最近哪个方向开始变化。"}
					</p>
					<div className="report-ranges">
						{report.ranges.map((range) => (
							<span key={range.days}>
								<b>{range.total}</b>
								最近 {range.days} 天
							</span>
						))}
					</div>
				</article>
			</section>

			<section className="section-block">
				<div className="section-title compact">
					<div>
						<span>成长档案</span>
						<h2>五维练习情况</h2>
					</div>
				</div>
				<div className="growth-card">
					{DIMENSIONS.map((dimension) => {
						const value = counts[dimension.id];
						const percent = Math.min(100, Math.round((value / 12) * 100));
						const stage =
							value >= 8 ? "稳定练习" : value >= 4 ? "正在形成" : value > 0 ? "刚开始" : "等待开始";
						return (
							<div className={cn("growth-row", value === 0 && "is-empty")} key={dimension.id}>
								<div className="growth-top">
									<strong>{dimension.name}</strong>
									<span>{stage}</span>
								</div>
								<div className="growth-track">
									<i style={{ background: dimension.color, width: `${percent}%` }} />
								</div>
								<em>
									{value > 0 ? `已完成 ${value} 次相关练习` : "完成后会显示这个方向的练习节奏"}
								</em>
							</div>
						);
					})}
				</div>
			</section>

			<section className="section-block">
				<div className="section-title compact">
					<div>
						<span>成长印记</span>
						<h2>练习章</h2>
					</div>
					<strong className="section-count">
						{badges.filter((badge) => badge.got).length}/{badges.length}
					</strong>
				</div>
				<div className="badge-grid">
					{badges.map((badge) => {
						const Icon = badge.got ? badge.Icon : LockKeyhole;
						return (
							<div
								className={cn("badge-card", badge.got ? "got" : "locked")}
								key={badge.id}
								style={
									{
										"--badge": DIM[badge.dim].color,
										"--badge-deep": DIM[badge.dim].deep,
										"--badge-soft": DIM[badge.dim].soft,
									} as React.CSSProperties
								}
							>
								<div className="badge-medal" aria-hidden="true">
									<i className="badge-ribbon left" />
									<i className="badge-ribbon right" />
									<div className="badge-icon">
										<Icon size={19} strokeWidth={2.4} />
									</div>
								</div>
								<strong>{badge.name}</strong>
								<span>{badge.desc}</span>
							</div>
						);
					})}
				</div>
			</section>

			<section className="section-block">
				<div className="section-title compact">
					<div>
						<span>我的关系</span>
						<h2>分享后的联结记录</h2>
					</div>
				</div>
				<div className="relation-list">
					{relations.length ? (
						relations.map((relation) => (
							<div className="relation-card" key={relation.id}>
								<Avatar label={relation.name.slice(0, 1)} size={42} />
								<div>
									<strong>{relation.name}</strong>
									<span>
										{relation.tag} · 来自「{relation.from}」
									</span>
								</div>
								<HandHeart className="relation-mark" size={18} />
							</div>
						))
					) : (
						<EmptyState
							icon={HandHeart}
							text="完成可分享练习后，可以把重要联结保存到这里。"
							title="还没有关系记录"
							tone="relate"
						/>
					)}
				</div>
			</section>

			<section className="section-block">
				<div className="section-title compact">
					<div>
						<span>我的小组</span>
						<h2>一起成长的人</h2>
					</div>
				</div>
				<div className="group-list">
					{groups.length ? (
						groups.map((group) => (
							<article
								className="group-card"
								key={group.id}
								style={
									{
										"--active-dim": DIM[group.dim].color,
										"--active-dim-soft": DIM[group.dim].soft,
									} as React.CSSProperties
								}
							>
								<div className="group-art" aria-hidden="true">
									<DimScene dim={group.dim} />
								</div>
								<div className="group-body">
									<DimChip dim={group.dim} quiet />
									<strong>{group.name}</strong>
									<p>{group.description}</p>
									<span className="group-session">
										{group.sessionLabel} · {group.sessionTitle} · {group.memberCount} 人
									</span>
									<button
										className={cn("ghost-action compact", group.joined && "checked")}
										disabled={group.joined}
										onClick={() => onJoinGroup(group.id)}
										type="button"
									>
										{group.joined ? "已加入" : "加入小组"}
									</button>
								</div>
							</article>
						))
					) : (
						<EmptyState
							icon={UsersRound}
							text="小组练习开放后，会在这里显示你加入的人和练习主题。"
							title="还没有加入小组"
							tone="relate"
						/>
					)}
				</div>
			</section>
		</section>
	);
};

const DrawFlow: React.FC<{
	keyword: string;
	todayPractice: Practice | null;
	onClose: () => void;
	onDraw: () => Practice | Promise<Practice>;
	onGo: (practice: Practice) => void;
}> = ({ keyword, onClose, onDraw, onGo, todayPractice }) => {
	const [stage, setStage] = useState<"shuffle" | "lock" | "result">(
		todayPractice ? "result" : "shuffle",
	);
	const [result, setResult] = useState<Practice | null>(todayPractice);
	const onDrawRef = useRef(onDraw);

	useEffect(() => {
		onDrawRef.current = onDraw;
	}, [onDraw]);

	useEffect(() => {
		if (!todayPractice) return;
		setResult(todayPractice);
		setStage("result");
	}, [todayPractice]);

	useEffect(() => {
		if (todayPractice || result) return undefined;

		let cancelled = false;
		const lockTimer = window.setTimeout(() => setStage("lock"), 1900);
		const revealTimer = window.setTimeout(() => {
			void Promise.resolve(onDrawRef.current())
				.then((next) => {
					if (cancelled) return;
					setResult(next);
					setStage("result");
				})
				.catch(() => {
					if (cancelled) return;
					setStage("shuffle");
				});
		}, 2850);

		return () => {
			cancelled = true;
			window.clearTimeout(lockTimer);
			window.clearTimeout(revealTimer);
		};
	}, [result, todayPractice]);

	const isLocking = stage === "lock";

	return (
		<div className={cn("flow-screen draw-flow", `draw-${stage}`)}>
			<button aria-label="返回首页" className="draw-back-button" onClick={onClose} type="button">
				<ArrowLeft size={22} />
			</button>
			<div className="draw-flow-body">
				{stage !== "result" && (
					<>
						<div className="dark-heading">
							<span>DAILY DRAW</span>
							<h1>今日 PERMA 训练</h1>
							<p>{isLocking ? "正在为你锁定今日练习" : "五个方向正在随机轮转"}</p>
						</div>
						<div className="draw-stage">
							<div className="card-fan">
								{["P", "E", "A", "M", "R"].map((letter, index) => (
									<div className={`draw-tile tile-${index}`} key={letter}>
										{letter}
									</div>
								))}
							</div>
							<div className="question-card">
								<strong>?</strong>
								<span>PERMA</span>
							</div>
						</div>
						<div className="draw-footer draw-loading-footer" aria-live="polite">
							<div className="draw-status">
								<p>{isLocking ? "已抽中，准备揭晓" : "正在随机抽取今日训练..."}</p>
								<span>
									{isLocking
										? "结果已锁定 · 即将打开"
										: keyword.trim()
											? `带着「${keyword.trim()}」开始`
											: "纯随机 · 不可预测"}
								</span>
							</div>
							<div className="draw-progress" aria-hidden="true">
								<i />
								<i />
								<i />
							</div>
						</div>
					</>
				)}
				{stage === "result" && result && (
					<>
						<div className="dark-heading">
							<span>DAILY DRAW</span>
							<h1>抽取完成</h1>
							<p>今天的练习已经为你落定</p>
						</div>
						<div className="draw-result-stage">
							<div
								className="draw-result-card"
								style={
									{
										"--active-dim": DIM[result.dim].color,
										"--active-dim-soft": DIM[result.dim].soft,
										"--active-dim-deep": DIM[result.dim].deep,
									} as React.CSSProperties
								}
							>
								<DimChip dim={result.dim} />
								<h1>{result.title}</h1>
								<span>{result.sub}</span>
								<p>{result.intro}</p>
							</div>
						</div>
						<div className="draw-footer result-footer">
							<div className="reveal-target">
								<span>今日目标</span>
								<p>{result.steps[0]}</p>
							</div>
							<small>已保存为今日练习 · 可回首页查看</small>
							<div className="result-actions">
								<button className="dark-secondary-action" onClick={onClose} type="button">
									回首页
								</button>
								<button className="dark-action" onClick={() => onGo(result)} type="button">
									开始练习
									<ArrowRight size={16} />
								</button>
							</div>
						</div>
					</>
				)}
			</div>
		</div>
	);
};

const PracticeDetail: React.FC<{
	evidenceRules: ApiEvidenceRule[];
	isCompleted: boolean;
	onClose: () => void;
	onComplete: (practice: Practice, payload: PracticePayload) => void;
	onRecord: (practice: Practice) => void;
	onViewRecords: () => void;
	practice: Practice;
	subskills: ApiSubskill[];
}> = ({
	evidenceRules,
	isCompleted,
	onClose,
	onComplete,
	onRecord,
	onViewRecords,
	practice,
	subskills,
}) => {
	const [courseOpen, setCourseOpen] = useState(false);
	const [assessmentOpen, setAssessmentOpen] = useState(false);
	const dimension = DIM[practice.dim];
	const dimensionRules = evidenceRules.filter((rule) => rule.dimensionId === practice.dim);
	const scoreRules = dimensionRules.filter((rule) => rule.ruleType === "score").slice(0, 4);
	const noScoreRules = dimensionRules.filter((rule) => rule.ruleType === "no_score").slice(0, 4);
	const dimensionSubskills = subskills
		.filter((item) => item.dimensionId === practice.dim)
		.slice(0, 4);
	return (
		<div className="flow-screen detail-flow">
			<div className="flow-head">
				<button className="icon-button" onClick={onClose} type="button">
					<ArrowLeft size={18} />
				</button>
				<strong>{dimension.name}</strong>
				<span className="head-spacer" />
			</div>
			<div className="detail-scroll">
				<section
					className="detail-hero"
					style={
						{
							"--active-dim": dimension.color,
							"--active-dim-soft": dimension.soft,
							"--active-dim-deep": dimension.deep,
						} as React.CSSProperties
					}
				>
					<DimChip dim={practice.dim} />
					<h1>{practice.title}</h1>
					<p>{practice.intro}</p>
				</section>
				{isCompleted && (
					<section className="detail-completed-card">
						<div className="completed-mark">
							<Check size={18} />
						</div>
						<div>
							<strong>今天这一步已完成</strong>
							<p>这条练习已经进入你的个人时间轴。可以回看记录，不需要重复提交。</p>
						</div>
					</section>
				)}
				<section className="steps-card action-steps-card">
					<div className="steps-card-head">
						<h2>练习动作</h2>
						<span>按这 3 步完成</span>
					</div>
					{practice.steps.map((step, index) => (
						<div className="step-row" key={`${practice.id}-${index}`}>
							<span>{index + 1}</span>
							<p>{step}</p>
						</div>
					))}
					<div className="record-hint">
						<span>记录时可以写</span>
						<p>{practice.recordPrompt}</p>
					</div>
				</section>
				<button
					className="support-card as-button"
					onClick={() => setCourseOpen((value) => !value)}
					type="button"
				>
					<div className="support-icon">
						<Play fill="currentColor" size={15} />
					</div>
					<div>
						<strong>{practice.course.title}</strong>
						<span>{practice.course.len} · 方法来源与练习要点</span>
					</div>
				</button>
				{courseOpen && (
					<section className="method-card">
						<h2>为什么做这个练习</h2>
						<p>{practice.source?.theorySource || practice.course.title}</p>
						<div className="method-grid">
							<span>形式：{practice.source?.formTag || "行动"}</span>
							<span>来源：{practice.source?.originalTheme || "积极心理学练习"}</span>
						</div>
						<p>{practice.intro}</p>
					</section>
				)}
				<button
					className="support-card muted as-button"
					onClick={() => setAssessmentOpen((value) => !value)}
					type="button"
				>
					<div className="support-icon">
						<CircleUserRound size={17} />
					</div>
					<div>
						<strong>{practice.assess?.title ?? `${dimension.name}自查`}</strong>
						<span>{dimensionSubskills.length || 4} 项 · 子能力自查</span>
					</div>
				</button>
				{assessmentOpen && (
					<section className="assessment-card">
						{dimensionSubskills.map((item) => (
							<div className="assessment-row" key={item.id}>
								<div>
									<strong>{item.name}</strong>
									<p>{item.definition}</p>
								</div>
								<span>1-5</span>
							</div>
						))}
					</section>
				)}
				{(scoreRules.length > 0 || noScoreRules.length > 0) && (
					<section className="evidence-rules-card">
						<div>
							<h2>什么会计入成长证据</h2>
							{scoreRules.map((rule) => (
								<p key={rule.id}>
									<Check size={13} />
									{rule.text}
								</p>
							))}
						</div>
						<div>
							<h2>不会简单计分</h2>
							{noScoreRules.map((rule) => (
								<p key={rule.id}>
									<X size={13} />
									{rule.text}
								</p>
							))}
						</div>
					</section>
				)}
			</div>
			<div className="detail-actions">
				{isCompleted ? (
					<>
						<button className="ghost-action" onClick={onClose} type="button">
							回首页
						</button>
						<button className="primary-action" onClick={onViewRecords} type="button">
							查看记录
						</button>
					</>
				) : (
					<>
						<button
							className="ghost-action"
							onClick={() => onComplete(practice, { mode: "text", text: "" })}
							type="button"
						>
							直接完成
						</button>
						<button className="primary-action" onClick={() => onRecord(practice)} type="button">
							记录一下
						</button>
					</>
				)}
			</div>
		</div>
	);
};

const RecordSheet: React.FC<{
	onClose: () => void;
	onDone: (practice: Practice, payload: PracticePayload) => void;
	practice: Practice;
}> = ({ onClose, onDone, practice }) => {
	const [text, setText] = useState("");
	const [mode, setMode] = useState<RecordMode>("text");
	const [recording, setRecording] = useState(false);
	const [seconds, setSeconds] = useState(0);

	useEffect(() => {
		if (!recording) return undefined;
		const timer = window.setInterval(() => setSeconds((value) => value + 1), 1000);
		return () => window.clearInterval(timer);
	}, [recording]);

	return (
		<div className="sheet-mask" onClick={onClose}>
			<div className="sheet record-sheet" onClick={(event) => event.stopPropagation()}>
				<div className="sheet-grip" />
				<div className="sheet-head">
					<button onClick={onClose} type="button">
						取消
					</button>
					<strong>完成练习</strong>
					<button
						className="ready"
						onClick={() =>
							onDone(practice, {
								mode,
								text: text.trim(),
								image: mode === "image" ? "paper" : null,
								voice: mode === "voice" ? Math.max(seconds, 10) : 0,
							})
						}
						type="button"
					>
						完成
					</button>
				</div>
				<DimChip dim={practice.dim} quiet />
				<h2>{practice.title}</h2>
				<div className="record-prompt-card">
					<span>可以这样写</span>
					<p>{practice.recordPrompt}</p>
				</div>
				<textarea
					onChange={(event) => setText(event.target.value)}
					placeholder="按上面的提示写几句；如果此刻不想写，也可以直接完成。"
					value={text}
				/>
				{mode === "image" && <div className="media-preview">图片上传占位</div>}
				{mode === "voice" && (
					<div className="voice-proof">
						<button
							className={recording ? "recording" : ""}
							onClick={() => setRecording((value) => !value)}
							type="button"
						>
							{recording ? <Pause size={18} /> : <Mic size={18} />}
						</button>
						<span>
							{recording ? "正在录音" : seconds > 0 ? `语音 ${seconds}"` : "点击开始录音"}
						</span>
					</div>
				)}
				<ModeTools mode={mode} onMode={setMode} />
			</div>
		</div>
	);
};

const NoteSheet: React.FC<{
	groups: GrowthGroup[];
	onClose: () => void;
	onPublish: (payload: PracticePayload) => void;
}> = ({ groups, onClose, onPublish }) => {
	const [text, setText] = useState("");
	const [mode, setMode] = useState<RecordMode>("text");
	const [visibility, setVisibility] = useState<NoteVisibility>("private");
	const [targetGroupId, setTargetGroupId] = useState("");
	const [friendName, setFriendName] = useState("");
	const [friendTag, setFriendTag] = useState(RELATION_TAGS[0]);
	const [recording, setRecording] = useState(false);
	const [seconds, setSeconds] = useState(0);
	const joinedGroups = groups.filter((group) => group.joined);
	const canSave =
		visibility === "group"
			? Boolean(targetGroupId)
			: visibility === "friend"
				? Boolean(friendName.trim())
				: true;

	useEffect(() => {
		if (!recording) return undefined;
		const timer = window.setInterval(() => setSeconds((value) => value + 1), 1000);
		return () => window.clearInterval(timer);
	}, [recording]);

	return (
		<div className="sheet-mask" onClick={onClose}>
			<div className="sheet note-sheet" onClick={(event) => event.stopPropagation()}>
				<div className="sheet-grip" />
				<div className="sheet-head">
					<button onClick={onClose} type="button">
						取消
					</button>
					<strong>随心记</strong>
					<button
						className="ready"
						disabled={!canSave}
						onClick={() =>
							onPublish({
								friendName: visibility === "friend" ? friendName.trim() : undefined,
								friendTag: visibility === "friend" ? friendTag : undefined,
								mode,
								text: text.trim(),
								image: mode === "image" ? "paper" : null,
								targetGroupId: visibility === "group" ? targetGroupId : null,
								visibility,
								voice: mode === "voice" ? Math.max(seconds, 10) : 0,
							})
						}
						type="button"
					>
						保存
					</button>
				</div>
				<textarea
					autoFocus
					className="note-textarea"
					onChange={(event) => setText(event.target.value)}
					placeholder="此刻在想什么？随手记下来。"
					value={text}
				/>
				<div className="note-destination">
					<span>保存到</span>
					<div className="destination-tabs">
						<button
							className={cn(visibility === "private" && "active")}
							onClick={() => setVisibility("private")}
							type="button"
						>
							仅自己
						</button>
						<button
							className={cn(visibility === "group" && "active")}
							onClick={() => {
								setVisibility("group");
								setTargetGroupId((value) => value || joinedGroups[0]?.id || "");
							}}
							type="button"
						>
							发到小组
						</button>
						<button
							className={cn(visibility === "friend" && "active")}
							onClick={() => setVisibility("friend")}
							type="button"
						>
							分享给朋友
						</button>
					</div>
					{visibility === "group" && (
						<div className="target-panel">
							{joinedGroups.length ? (
								<div className="group-choice-list">
									{joinedGroups.map((group) => (
										<button
											className={cn(targetGroupId === group.id && "active")}
											key={group.id}
											onClick={() => setTargetGroupId(group.id)}
											type="button"
										>
											<strong>{group.name}</strong>
											<span>{group.sessionTitle}</span>
										</button>
									))}
								</div>
							) : (
								<p>先在“我的”里加入一个小组，再发布给小组成员。</p>
							)}
						</div>
					)}
					{visibility === "friend" && (
						<div className="target-panel">
							<label className="share-field compact">
								<span>好友昵称</span>
								<input
									onChange={(event) => setFriendName(event.target.value)}
									placeholder="例如：小满"
									value={friendName}
								/>
							</label>
							<div className="relation-tags compact">
								{RELATION_TAGS.map((item) => (
									<button
										className={cn(friendTag === item && "active")}
										key={item}
										onClick={() => setFriendTag(item)}
										type="button"
									>
										{item}
									</button>
								))}
							</div>
							<div className="share-poster compact" aria-hidden="true">
								<strong>随心记分享海报</strong>
								<p>{text.trim() || "写完后会生成一张可分享的小卡片。"}</p>
							</div>
						</div>
					)}
				</div>
				{mode === "image" && <div className="media-preview">图片上传占位</div>}
				{mode === "voice" && (
					<div className="voice-proof">
						<button
							className={recording ? "recording" : ""}
							onClick={() => setRecording((value) => !value)}
							type="button"
						>
							{recording ? <Pause size={18} /> : <Mic size={18} />}
						</button>
						<span>
							{recording ? "正在录音" : seconds > 0 ? `语音 ${seconds}"` : "点击开始录音"}
						</span>
					</div>
				)}
				<ModeTools mode={mode} onMode={setMode} />
			</div>
		</div>
	);
};

const ModeTools: React.FC<{ mode: RecordMode; onMode: (mode: RecordMode) => void }> = ({
	mode,
	onMode,
}) => {
	const tools: Array<{ id: RecordMode; label: string; Icon: LucideIcon }> = [
		{ id: "text", label: "文字", Icon: Type },
		{ id: "image", label: "图片", Icon: ImageIcon },
		{ id: "voice", label: "语音", Icon: Mic },
	];
	return (
		<div className="tool-grid">
			{tools.map(({ Icon, id, label }) => (
				<button
					className={cn(mode === id && "active")}
					key={id}
					onClick={() => onMode(id)}
					type="button"
				>
					<Icon size={18} />
					{label}
				</button>
			))}
		</div>
	);
};

const SuccessOverlay: React.FC<{
	entry: TimelineItem;
	evidenceScore?: number;
	nextAction?: string;
	nextSuggestion?: string;
	onClose: () => void;
	onFeedback: (score: number, reason: string) => void;
	onHome: () => void;
	onShare: () => void;
	practice: Practice;
}> = ({
	entry,
	evidenceScore,
	nextAction,
	nextSuggestion,
	onClose,
	onFeedback,
	onHome,
	onShare,
	practice,
}) => (
	<div className="sheet-mask success-mask">
		<div className="success-card">
			<button className="success-close" onClick={onClose} type="button">
				<X size={18} />
			</button>
			<div className="success-medal">
				<Check size={26} />
			</div>
			<h1>今天这一步完成了</h1>
			<p>「{practice.title}」已经保存到你的个人时间轴。</p>
			<div className="success-evidence">
				<span>记录具体度</span>
				<strong>{evidenceScore ?? entry.feedbackScore ?? 3}/5</strong>
				<p>{nextSuggestion ?? "这条记录会进入你的成长档案，帮助后续推荐更贴近你。"}</p>
				{nextAction && (
					<em>
						{nextAction === "switch"
							? "下次换个更好记录的动作"
							: nextAction === "upgrade"
								? "可进入专项强化"
								: "建议继续观察"}
					</em>
				)}
			</div>
			<div className="feedback-row">
				<button onClick={() => onFeedback(5, "fit")} type="button">
					适合我
				</button>
				<button onClick={() => onFeedback(3, "neutral")} type="button">
					一般
				</button>
				<button onClick={() => onFeedback(1, "switch")} type="button">
					想换一个
				</button>
			</div>
			<div className="success-preview">
				<TimelineCard item={entry} />
			</div>
			<div className="success-actions">
				<button className="ghost-action" onClick={onHome} type="button">
					回首页继续今日任务
				</button>
				{practice.share && (
					<button className="primary-action" onClick={onShare} type="button">
						分享给好友
					</button>
				)}
			</div>
		</div>
	</div>
);

const ShareSheet: React.FC<{
	entry: TimelineItem;
	onClose: () => void;
	onShared: (asset: { name: string; tag: string; visibility: string }) => void;
}> = ({ entry, onClose, onShared }) => {
	const [name, setName] = useState("");
	const [tag, setTag] = useState(RELATION_TAGS[0]);
	return (
		<div className="sheet-mask" onClick={onClose}>
			<div className="sheet share-sheet" onClick={(event) => event.stopPropagation()}>
				<div className="sheet-grip" />
				<div className="sheet-head">
					<button onClick={onClose} type="button">
						取消
					</button>
					<strong>分享给微信好友</strong>
					<span />
				</div>
				<p className="share-summary">
					生成「{entry.title ?? "练习记录"}」海报后，可以转发给微信好友，并沉淀一条关系记录。
				</p>
				<div className="share-poster">
					<span>练习分享海报</span>
					<strong>{entry.title ?? "今天的真实行动"}</strong>
					<p>{entry.text}</p>
				</div>
				<label className="share-field">
					<span>对方昵称</span>
					<input
						onChange={(event) => setName(event.target.value)}
						placeholder="例如：小满"
						value={name}
					/>
				</label>
				<div className="relation-tags">
					{RELATION_TAGS.map((item) => (
						<button
							className={cn(tag === item && "active")}
							key={item}
							onClick={() => setTag(item)}
							type="button"
						>
							{item}
						</button>
					))}
				</div>
				<button
					className="primary-action full"
					disabled={!name.trim()}
					onClick={() => onShared({ name: name.trim(), tag, visibility: "friend" })}
					type="button"
				>
					生成海报并记录
				</button>
			</div>
		</div>
	);
};
